import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Dropdown, Modal, Button, Form } from 'react-bootstrap';
import { Line } from 'react-chartjs-2';
import { Chart } from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import Slider from '@mui/material/Slider';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import DeleteIcon from '@mui/icons-material/Delete';
import Input from '@mui/material/Input';
import 'chart.js/auto';
import Carousel from 'react-multi-carousel';
import 'react-multi-carousel/lib/styles.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import { format } from 'date-fns'; // For date formatting
import Cookies from 'js-cookie';
import axios from 'axios';
import { color } from 'highcharts';
import { RotatingLines } from "react-loader-spinner";
import { motion } from "framer-motion";
import { Lock, Unlock } from "lucide-react";

// Register the plugin
Chart.register(annotationPlugin);

const API_URL = `https://options.healthsaving.life/api/`;

// Utility to generate unique colors
function generateUniqueColors(count) {
  const colors = [];
  for (let i = 0; i < count; i++) {
    const hue = (i * 137.5) % 360; // Distribute hues evenly
    colors.push(`hsl(${hue}, 70%, 50%)`); // Use HSL for vivid colors
  }
  return colors;
}

// Fetch acct_names from the API
const fetchacct_names = async () => {
  let api_url = `${API_URL}get_option_chain_ibkr_positions_acct_name.php`;
  
  const response = await fetch(`${api_url}`);

  const data = await response.json();
  return data.data.map(item => item.acct_name);
};

// Fetch symbols from the API
const fetchSymbols = async (portfolioMode,selectedacct_name) => {
  let api_url = `${API_URL}get_option_chain_ibkr_positions_symbol.php`;

  if (portfolioMode) {
    api_url = `${API_URL}get_option_chain_ibkr_positions_symbol.php?acct_name=${selectedacct_name}`;
  }

  const response = await fetch(`${api_url}`);

  const data = await response.json();
  return data.data.map(item => item.symbol);
};

// Fetch expiry dates based on the selected symbol
const fetchExpiries = async (symbol, portfolioMode,selectedacct_name) => {
  if (symbol) {

    let api_url = `${API_URL}get_option_chain_ibkr_positions_expiry.php?symbol=${symbol}`;
    if (portfolioMode) {
      api_url = `${API_URL}get_option_chain_ibkr_positions_expiry.php?symbol=${symbol}&acct_name=${selectedacct_name}`;
    }

    const response = await fetch(`${api_url}`);
    const data = await response.json();
    if (data) {
      return data;
      // return data.data.map(item => item.expiry);
    } else {
      return [];
    }
  } else {
    return [];
  }
};


// Fetch expiry dates based on the selected symbol
const fetchStrikePriceRange = async (symbol, expiry, portfolioMode,selectedacct_name) => {

  if (symbol) {

    let api_url = `${API_URL}get_option_chain_ibkr_positions_data.php?symbol=${symbol}`;

    if (portfolioMode) {
      api_url = `${API_URL}get_option_chain_ibkr_positions_data.php?symbol=${symbol}&acct_name=${selectedacct_name}`;

      if (expiry) {
        api_url = `${API_URL}get_option_chain_ibkr_positions_data.php?symbol=${symbol}&expiry=${expiry}&acct_name=${selectedacct_name}`;
      }
    }

    const response = await fetch(`${api_url}`);
    const data = await response.json();
    return data.data;

  } else {
    return [];
  }
};

// Fetch portfolio data from the API
const fetchPortfolio = async (symbol, expiry,selectedacct_name) => {

  // console.log('symbol',symbol);
  if (symbol) {
    const response = await fetch(`${API_URL}get_option_chain_ibkr_positions.php?symbol=${symbol}&expiry=${expiry}&acct_name=${selectedacct_name}`);
    const data = await response.json();
    // console.log('fetch get_option_chain_ibkr_positions:', data);
    if (data.result) {
      return data.data[0];
    } else {
      return { symbol: '', expiry: '', strikes: '[{"type":"Buy","values":[]},{"type":"Sell","values":[]}]', underlying_data: [],avg_price_data: [] }
    }
  } else {
    return { symbol: '', expiry: '', strikes: '[{"type":"Buy","values":[]},{"type":"Sell","values":[]}]', underlying_data: [],avg_price_data: [] }
  }
};

// // Function to simulate fetching strike prices based on symbol and expiry
const fetchStrikePrices = (symbol, expiry) => {
  return [
    { type: 'Buy', values: [] },
    { type: 'Sell', values: [] },
  ];
};


// Helper function to calculate the cumulative normal distribution function (CDF)
const cumulativeNormalDistribution = (x) => {
  const p = 0.2316419;
  const b1 = 0.319381530;
  const b2 = -0.356563782;
  const b3 = 1.781477937;
  const b4 = -1.821255978;
  const b5 = 1.330274429;

  // Handle the symmetry for negative values of x
  const sign = x < 0 ? -1 : 1;
  x = Math.abs(x);

  const t = 1 / (1 + p * x);
  const poly = (((((b5 * t + b4) * t + b3) * t + b2) * t + b1) * t);
  const d = 1 - (Math.exp(-x * x / 2) / Math.sqrt(2 * Math.PI)) * poly;

  return sign === 1 ? d : 1 - d;
};


// Black-Scholes pricing function
const calculateBlackScholes = (S, K, T, r, sigma, type) => {
  const d1 = (Math.log(S / K) + (r + (sigma ** 2) / 2) * T) / (sigma * Math.sqrt(T));
  const d2 = d1 - sigma * Math.sqrt(T);

  const callPrice = S * cumulativeNormalDistribution(d1) - K * Math.exp(-r * T) * cumulativeNormalDistribution(d2);
  const putPrice = K * Math.exp(-r * T) * cumulativeNormalDistribution(-d2) - S * cumulativeNormalDistribution(-d1);

    // Check if any calculation results in NaN and return 0 if so
    if (isNaN(callPrice) || isNaN(putPrice)) {
      return 0;
    }

  return type === 'call' ? callPrice : putPrice;

  // if(S>K){
  //   return type === 'call' ? 0 : -putPrice;
  // }else{
  //   return type === 'call' ? -callPrice : 0;
  // }
};

// Function to generate option data for the graph
const generateOptionsData = (stockPrice, minPrice, maxPrice, strikes, allStrikes, displayAsPercentage, contractSize, date, impliedVolatility, underlyingData,NewriskFreeRate,thumbMoved,selectedSymbol) => { //used static dummy data here
  
  // console.log('CMP contractSize:',contractSize);
  // console.log('Current strikes:', strikes);
  // console.log('all strikes:', allStrikes);
  // console.log('Current underlyingData:', underlyingData);
  // console.log('selectedSymbol:', selectedSymbol);

  // console.log(date);
  // console.log(impliedVolatility);

  if(true){
  // if(strikes.length>0){
  

  const data = [];
  const step = 1;
  const premiumReceived = 0;
  const maxLoss = 0;

  let optionPrice = 0;
  let optionValue1 = 0;
  let optionValue2 = 0;
  let optionValue3 = 0;
  let optionValue4 = 0;
  let optionValue5 = 0;
  let optionValue6 = 0;

  let PD1 = 0;
  let PD2 = 0;
  let PD3 = 0;
  let PD4 = 0;

  let oV1 = 0;
  let oV2 = 0;
  let oV3 = 0;
  let oV4 = 0;
  let oV5 = 0;
  let oV6 = 0;

  let bs1 = 0;
  let bs2 = 0;
  let bs3 = 0;
  let bs4 = 0;

  let all_optionPrice = 0;
  let all_optionValue1 = 0;
  let all_optionValue2 = 0;
  let all_optionValue3 = 0;
  let all_optionValue4 = 0;
  let all_optionValue5 = 0;
  let all_optionValue6 = 0;

  let all_PD1 = 0;
  let all_PD2 = 0;
  let all_PD3 = 0;
  let all_PD4 = 0;

  let all_oV1 = 0;
  let all_oV2 = 0;
  let all_oV3 = 0;
  let all_oV4 = 0;
  let all_oV5 = 0;
  let all_oV6 = 0;

  let all_bs1 = 0;
  let all_bs2 = 0;
  let all_bs3 = 0;
  let all_bs4 = 0;

  let riskFreeRate = NewriskFreeRate/100; //0.0379
  let stockPriceCMP = stockPrice;
  let impliedVolatilityPer = impliedVolatility / 100;
  let data_push=false;
  let equity_draw=false;
  // let T = date / 365;

  // for (stockPrice; stockPrice <= maxPrice; stockPrice += step) {
    for (let stockPrice = minPrice; stockPrice <= maxPrice; stockPrice += step) {
      let payoff = premiumReceived;
      let all_payoff = premiumReceived;
      let premium_deployed = 0;
      let equity = 0;
    // console.log('Current payoff before:',payoff);


    
    allStrikes.forEach((strike) => {

      if(strike.symbol==selectedSymbol){
        data_push=true;

        // if (!expiries.includes(expiryDate)) {
        //   expiries.push(expiryDate);
        // }

      const lotMultiplier = strike.quantity ?? 1; // Default to 1 if quantity is undefined
      const avg_price_share = (thumbMoved)?strike.avg_price_share_new:strike.avg_price_share; 
      const shares = lotMultiplier * contractSize; 
      const underlying_shares = strike.shares ?? 0; 
      const strike_expiry = strike.expiry ?? 0; 
      const strike_value = strike.value ?? 0; 
      const strike_iv = (impliedVolatilityPer===0)?strike.iv:impliedVolatilityPer;
      
      // console.log('avg_price_share:',avg_price_share);

    const date1Ms = new Date(strike_expiry).getTime();
    const date2Ms = new Date().getTime();

    const differenceMs = Math.abs(date2Ms - date1Ms);
    const differenceDays = Math.floor(differenceMs / (1000 * 60 * 60 * 24));

      // console.log('strike_expiry:',strike_expiry,differenceDays);
      let T = (differenceDays-date)/365;

      if (strike.type === 'Buy Call') {

        all_PD1 = all_PD1 + shares * avg_price_share;

        if (date === 0) {
          all_optionPrice = (stockPrice > strike_value) ? stockPrice - strike_value : 0;
          all_oV1 = shares * (all_optionPrice - avg_price_share);
          // console.log('strike Buy Call - stockPrice,shares,optionPrice,avg_price_share,optionValue', stockPrice, shares, optionPrice, avg_price_share, oV1);
          all_optionValue1 = all_optionValue1 + all_oV1;
          all_oV1 = 0;
        } else {
          all_bs1 = calculateBlackScholes( stockPrice,strike_value, T, riskFreeRate, strike_iv, 'call');
          all_oV1 = shares * (all_bs1-avg_price_share);
          all_optionValue1 = all_optionValue1 + all_oV1;
          // console.log('Buy Call -  Current market price,Strike price,Time(years),Risk Free Rate,Implied Volatility,Type,BS', stockPrice.toFixed(1),strike_value, T, riskFreeRate, strike_iv, 'call', bs1);
          all_oV1 = 0;
        }

        // console.log('all_optionValue1:',all_optionValue1);

      } else if (strike.type === 'Buy Put') {
        all_PD2 = all_PD2 + shares * avg_price_share;
        if (date === 0) {
          all_optionPrice = (strike_value > stockPrice) ? strike_value - stockPrice : 0;
          all_oV2 = shares * (all_optionPrice - avg_price_share);
          // console.log('strike Buy Put - stockPrice,shares,optionPrice,avg_price_share,optionValue', stockPrice, shares, optionPrice, avg_price_share, oV2);
          all_optionValue2 = all_optionValue2 + all_oV2;
          all_oV2 = 0;
        } else {
          all_bs2 = calculateBlackScholes(stockPrice,strike_value, T, riskFreeRate, strike_iv, 'put');
          all_oV2 = shares * (all_bs2 -avg_price_share);
          all_optionValue2 = all_optionValue2 + all_oV2;
          // console.log('Buy Put - Current market price,Strike price,Time(years),Risk Free Rate,Implied Volatility,Type,BS', stockPrice.toFixed(1),strike_value, T, riskFreeRate, strike_iv, 'put', bs2);
          all_oV2 = 0;
        }
      } else if (strike.type === 'Sell Call') {
        all_PD3 = all_PD3 + shares * avg_price_share;
        if (date === 0) {
          all_optionPrice = (stockPrice > strike_value) ? stockPrice - strike_value : 0;
          all_oV3 = shares * (all_optionPrice - avg_price_share);
          // console.log('strike Sell Call - stockPrice,shares,optionPrice,avg_price_share,optionValue', stockPrice, shares, optionPrice, avg_price_share, oV3);
          all_optionValue3 = all_optionValue3 + all_oV3;
          all_oV3 = 0;
        } else {
          all_bs3 = calculateBlackScholes(stockPrice,strike_value, T, riskFreeRate, strike_iv, 'call');
          all_oV3 = shares * (all_bs3 -avg_price_share);
          all_optionValue3 = all_optionValue3 + all_oV3;
          // console.log('Sell Call - Current market price,Strike price,Time(years),Risk Free Rate,Implied Volatility,Type,BS', stockPrice.toFixed(1),strike_value, T, riskFreeRate, strike_iv, 'call',  bs3);
          all_oV3 = 0;
        }
      } else if (strike.type === 'Sell Put') {
        all_PD4 = all_PD4 + shares * avg_price_share;
        if (date === 0) {
          all_optionPrice = (strike_value > stockPrice) ? strike_value - stockPrice : 0;
          all_oV4 = shares * (all_optionPrice - avg_price_share);
          // console.log('strike Sell Put - stockPrice,shares,optionPrice,avg_price_share,optionValue', stockPrice, shares, optionPrice, avg_price_share, oV4);
          all_optionValue4 = all_optionValue4 + all_oV4;
          all_oV4 = 0;
        } else {
          all_bs4 = calculateBlackScholes(stockPrice,strike_value, T, riskFreeRate, strike_iv, 'put');
          all_optionValue4 = shares * (all_bs4 - avg_price_share);
          all_oV4 = all_optionValue4 + all_oV4;
          // console.log('Sell Put - Current market price,Strike price,Time(years),Risk Free Rate,Implied Volatility,Type,BS', stockPrice.toFixed(1),strike_value, T, riskFreeRate, strike_iv, 'put',  bs4);
          all_oV4 = 0;
        }
      }
    }
    
    });
    
    strikes.forEach((strike) => {

      if(strike.symbol==selectedSymbol){
        data_push=true;

        // if (!expiries.includes(expiryDate)) {
        //   expiries.push(expiryDate);
        // }

      const lotMultiplier = strike.quantity ?? 1; // Default to 1 if quantity is undefined
      const avg_price_share = (thumbMoved)?strike.avg_price_share_new:strike.avg_price_share; 
      const shares = lotMultiplier * contractSize; 
      const underlying_shares = strike.shares ?? 0; 
      const strike_expiry = strike.expiry ?? 0; 
      const strike_value = strike.value ?? 0; 
      const strike_iv = (impliedVolatilityPer===0)?strike.iv:impliedVolatilityPer;
      
      // console.log('avg_price_share:',avg_price_share);

    const date1Ms = new Date(strike_expiry).getTime();
    const date2Ms = new Date().getTime();

    const differenceMs = Math.abs(date2Ms - date1Ms);
    const differenceDays = Math.floor(differenceMs / (1000 * 60 * 60 * 24));

      // console.log('strike_expiry:',strike_expiry,differenceDays);
      let T = (differenceDays-date)/365;

      if (strike.type === 'Buy Call') {

        PD1 = PD1 + shares * avg_price_share;

        if (date === 0) {
          optionPrice = (stockPrice > strike_value) ? stockPrice - strike_value : 0;
          oV1 = shares * (optionPrice - avg_price_share);
          // console.log('strike Buy Call - stockPrice,shares,optionPrice,avg_price_share,optionValue', stockPrice, shares, optionPrice, avg_price_share, oV1);
          optionValue1 = optionValue1 + oV1;
          oV1 = 0;
        } else {
          bs1 = calculateBlackScholes( stockPrice,strike_value, T, riskFreeRate, strike_iv, 'call');
          oV1 = shares * (bs1-avg_price_share);
          optionValue1 = optionValue1 + oV1;
          console.log('Buy Call -  Current market price,Strike price,Time(years),Risk Free Rate,Implied Volatility,Type,BS', stockPrice.toFixed(1),strike_value, T, riskFreeRate, strike_iv, 'call', bs1);
          oV1 = 0;
        }

        // console.log('optionValue1:',optionValue1);

      } else if (strike.type === 'Buy Put') {
        PD2 = PD2 + shares * avg_price_share;
        if (date === 0) {
          optionPrice = (strike_value > stockPrice) ? strike_value - stockPrice : 0;
          oV2 = shares * (optionPrice - avg_price_share);
          // console.log('strike Buy Put - stockPrice,shares,optionPrice,avg_price_share,optionValue', stockPrice, shares, optionPrice, avg_price_share, oV2);
          optionValue2 = optionValue2 + oV2;
          oV2 = 0;
        } else {
          bs2 = calculateBlackScholes(stockPrice,strike_value, T, riskFreeRate, strike_iv, 'put');
          oV2 = shares * (bs2 -avg_price_share);
          optionValue2 = optionValue2 + oV2;
          console.log('Buy Put - Current market price,Strike price,Time(years),Risk Free Rate,Implied Volatility,Type,BS', stockPrice.toFixed(1),strike_value, T, riskFreeRate, strike_iv, 'put', bs2);
          oV2 = 0;
        }
      } else if (strike.type === 'Sell Call') {
        PD3 = PD3 + shares * avg_price_share;
        if (date === 0) {
          optionPrice = (stockPrice > strike_value) ? stockPrice - strike_value : 0;
          oV3 = shares * (optionPrice - avg_price_share);
          // console.log('strike Sell Call - stockPrice,shares,optionPrice,avg_price_share,optionValue', stockPrice, shares, optionPrice, avg_price_share, oV3);
          optionValue3 = optionValue3 + oV3;
          oV3 = 0;
        } else {
          bs3 = calculateBlackScholes(stockPrice,strike_value, T, riskFreeRate, strike_iv, 'call');
          oV3 = shares * (bs3 -avg_price_share);
          optionValue3 = optionValue3 + oV3;
          console.log('Sell Call - Current market price,Strike price,Time(years),Risk Free Rate,Implied Volatility,Type,BS', stockPrice.toFixed(1),strike_value, T, riskFreeRate, strike_iv, 'call',  bs3);
          oV3 = 0;
        }
      } else if (strike.type === 'Sell Put') {
        PD4 = PD4 + shares * avg_price_share;
        if (date === 0) {
          optionPrice = (strike_value > stockPrice) ? strike_value - stockPrice : 0;
          oV4 = shares * (optionPrice - avg_price_share);
          // console.log('strike Sell Put - stockPrice,shares,optionPrice,avg_price_share,optionValue', stockPrice, shares, optionPrice, avg_price_share, oV4);
          optionValue4 = optionValue4 + oV4;
          oV4 = 0;
        } else {
          bs4 = calculateBlackScholes(stockPrice,strike_value, T, riskFreeRate, strike_iv, 'put');
          optionValue4 = shares * (bs4 - avg_price_share);
          oV4 = optionValue4 + oV4;
          console.log('Sell Put - Current market price,Strike price,Time(years),Risk Free Rate,Implied Volatility,Type,BS', stockPrice.toFixed(1),strike_value, T, riskFreeRate, strike_iv, 'put',  bs4);
          oV4 = 0;
        }
      }
    }

    });



    if (Array.isArray(underlyingData)) {
    underlyingData.forEach((underlying) => {

      const avg_price_share = underlying.avg_price_share ?? 0; // Default to 1 if avg_price_share is undefined
      const underlying_shares = underlying.shares ?? 0; // Default to 0 if avg_price_share is undefined

      if (underlying.side === 'B') {
        oV5 = underlying_shares * (stockPrice - avg_price_share);
        // console.log('Buy underlying - stockPrice,shares,avg_price_share,Value', stockPrice, underlying_shares, avg_price_share, oV5);
        optionValue5 = optionValue5 + oV5;
        oV5 = 0;
      } else if (underlying.side === 'S') {
        oV6 = underlying_shares * (stockPrice - avg_price_share);
        // console.log('Buy underlying - stockPrice,shares,avg_price_share,Value', stockPrice, underlying_shares, avg_price_share, oV6);
        optionValue6 = optionValue6 + oV6;
        oV6 = 0;
      }

      data_push=true;

    });
  } else {
    console.error("underlyingData is not an array:", underlyingData);
  }    

    // console.log('Current payoff after:',payoff);  //show points in graph
    // console.log('underlyingData:',underlyingData);  //show points in graph
    // console.log('optionValue1:',optionValue1);  //show points in graph
    // console.log('all_optionValue1:',all_optionValue1);  //show points in graph

    payoff = optionValue1 + optionValue2 - optionValue3 - optionValue4 + optionValue5 + optionValue6;
    all_payoff = all_optionValue1 + all_optionValue2 - all_optionValue3 - all_optionValue4 + optionValue5 + optionValue6;

    premium_deployed =  PD3 + PD4 - PD1 - PD2;

    equity = optionValue5 - optionValue6;
    if(equity!=0){
      equity_draw=true;
    }

    // if (date === 0) {
    // } else {
    //   payoff = optionValue1 + optionValue2 - optionValue3 - optionValue4;
    // }

    // console.log('Current payoff:', stockPrice, payoff);  //show points in graph

    // Convert payoff to percentage if displayAsPercentage is true
    // const payoffPercent = ((payoff / premiumReceived) * 100).toFixed(2);

    if(data_push){

      data.push({
        stockPrice,
        payoff: payoff,
        all_payoff: all_payoff,
        premium_deployed: premium_deployed,
        symbol: selectedSymbol,
        equity: equity,
        equity_draw: equity_draw,
        // payoff: displayAsPercentage ? payoffPercent : payoff,
      });
    }

    payoff = 0;
    premium_deployed = 0;

    optionValue1 = 0;
    optionValue2 = 0;
    optionValue3 = 0;
    optionValue4 = 0;
    optionValue5 = 0;
    optionValue6 = 0;

    PD1 = 0;
    PD2 = 0;
    PD3 = 0;
    PD4 = 0;

    all_optionValue1 = 0;
    all_optionValue2 = 0;
    all_optionValue3 = 0;
    all_optionValue4 = 0;
    all_optionValue5 = 0;
    all_optionValue6 = 0;

    all_PD1 = 0;
    all_PD2 = 0;
    all_PD3 = 0;
    all_PD4 = 0;

  }

  return data;
  }else{
    return [];
  }
};

// Function to calculate metrics for the options strategy
const calculateMetrics = (strikes = [], impliedVolatility, date) => { // Default to empty array for strikes
  const optionPremiums = { 'Buy Call': 5, 'Sell Call': 5, 'Buy Put': 5, 'Sell Put': 5 };
  let totalDebit = 0;
  let totalCredit = 0;

  // Ensure strikes is a valid array and process its values safely
  strikes.forEach((strike) => {
    if (strike.values && Array.isArray(strike.values)) { // Check if values is a valid array
      strike.values.forEach((value) => {
        const premium = (optionPremiums[value.type] ?? 0) * (value.quantity ?? 1); // Safe access with defaults
        if (value.type.includes('Buy')) {
          totalDebit += premium;
        } else if (value.type.includes('Sell')) {
          totalCredit += premium;
        }
      });
    }
  });

  // Calculate metrics
  const netDebit = totalDebit - totalCredit;
  const maxLoss = Math.max(totalDebit, 0);
  const maxProfit = Math.max(totalCredit, 0);
  const chanceOfProfit = '50%'; // Placeholder; would need actual calculation

  // Safely calculate minStrike and maxStrike with fallback to prevent errors
  const allStrikeValues = strikes.flatMap((s) => 
    Array.isArray(s.values) ? s.values.map((v) => v.value ?? 0) : []
  );
  const minStrike = allStrikeValues.length > 0 ? Math.min(...allStrikeValues) : 0;
  const maxStrike = allStrikeValues.length > 0 ? Math.max(...allStrikeValues) : 0;
  const breakEven = (minStrike + maxStrike) / 2;

  return { netDebit, maxLoss, maxProfit, chanceOfProfit, breakEven };
};


// Main component for options graph and multi-strike slider
const OptionsGraphWithMultiStrikeSlider = () => {

  const checkFirstRender1 = useRef(true);
  const checkFirstRender2 = useRef(true);
  // const checkFirstRender3 = useRef(true);
  // const checkFirstRender4 = useRef(true);
  // const checkFirstRender5 = useRef(true);
  // const checkFirstRender6 = useRef(true);
  // const checkFirstRender7 = useRef(true);

  const navigate = useNavigate(); // For URL manipulation
  const location = useLocation(); // To access current URL

  const [symbols, setSymbols] = useState([]);
  const [expiries, setExpiries] = useState([]);
  const [allExpiries, setAllExpiries] = useState([]);
  const [expiryColors, setExpiryColors] = useState({});
  const [maxExpiry, setMaxExpiry] = useState(null);
  const [maxDays, setMaxDays] = useState(30);
  const [strikes, setStrikes] = useState([]);
  const [strikesChange, setStrikesChange] = useState([]);
  const [selectedSymbol, setSelectedSymbol] = useState('');
  const [acct_names, setacct_names] = useState(['Walleyhouse']);
  const [selectedacct_name, setSelectedacct_name] = useState('Walleyhouse');
  const [selectedExpiry, setSelectedExpiry] = useState([]);
  const [minStrikePrice, setMinStrikePrice] = useState(0);
  const [maxStrikePrice, setMaxStrikePrice] = useState(0);
  const [strike_range, setstrike_range] = useState(0);
  const [stockPrice, setStockPrice] = useState(0);
  const [stockPriceCMP, setStockPriceCMP] = useState(0);
  const [range, setRange] = useState([0, 100]);
  const [sliderRange, setSliderRange] = useState(1);
  const [impliedVolatility, setImpliedVolatility] = useState(0);
  const [riskFreeRate, setriskFreeRate] = useState(3.79);
  const [date, setDate] = useState(0);
  const [debouncedDate, setDebouncedDate] = useState(date); // Debounced date value

  let timeoutId = null;

  const [anchorEl, setAnchorEl] = useState(null);
  const [currentTypeIndex, setCurrentTypeIndex] = useState(null);
  const [currentGroupIndex, setCurrentGroupIndex] = useState(null);
  const [currentValueIndex, setCurrentValueIndex] = useState(null);
  const [displayAsPercentage, setDisplayAsPercentage] = useState(false);
  const [currentStrikeValue, setCurrentStrikeValue] = useState(0);
  const [currentStrikePrice, setCurrentStrikePrice] = useState(0);
  const [currentStriketype, setCurrentStriketype] = useState(0);
  const [currentStrikeExpiry, setCurrentStrikeExpiry] = useState(0);
  const [currentStrikeavg_price_share, setCurrentStrikeavg_price_share] = useState(0);
  const [price, setPrice] = useState(0);

  const [bidprice, setBidPrice] = useState(0);
  const [askprice, setAskPrice] = useState(0);
  const [volume, setVolume] = useState(0);
  const [oi, setOI] = useState(0);
  const [iv, setIV] = useState(0);
  const [delta, setDelta] = useState(0);
  const [theta, setTheta] = useState(0);
  const [gamma, setGamma] = useState(0);
  const [vega, setVega] = useState(0);
  const [rho, setRho] = useState(0);

  const [portfolioMode, setPortfolioMode] = useState(true); // Portfolio or Custom mode
  const [searchParamsReact, setSearchParams] = useSearchParams();
  const [contractSize, setContractSize] = useState(100);
  const [hideExpiry, setHideExpiry] = useState('block');
  const [hideACCName, setHideACCName] = useState('block');

  // const [screenWidth, setScreenWidth] = useState(2000);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);

  const [min, setMin] = useState(0); // Minimum value
  const [max, setMax] = useState(0); // Maximum value
  const sliderWidth = screenWidth; // Set a fixed width for the slider container

  const [max_profit, setmax_profit] = useState(0);
  const [premium_deployed, setpremium_deployed] = useState(0);

  const [underlyingData, setunderlyingData] = useState([]);
  const [avgPriceData, setavgPriceData] = useState([]);

  const [showContextMenu, setShowContextMenu] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });
  const [currentEditIndex, setCurrentEditIndex] = useState(null);
  const [editValues, setEditValues] = useState({ shares: '', avg_price_share: '' });

  const [showPopup, setShowPopup] = useState(false);
  const [optionsSelectedExpiry, setOptionsSelectedExpiry] = useState('');
  const [popupTypeIndex, setPopupTypeIndex] = useState(null);
  const [popupStrikeType, setPopupStrikeType] = useState('');
  
  const [optionsStrikes, setOptionsStrikes] = useState([]);
  const [selectedStrike, setSelectedStrike] = useState('');

const [showPopupUnderlying, setShowPopupUnderlying] = useState(false);
const [popupShares, setPopupShares] = useState(100);
const [popupAvgPriceShare, setPopupAvgPriceShare] = useState(stockPrice);
const [popupTypeUnderlying, setPopupTypeUnderlying] = useState('');

const [loading, setLoading] = useState(false);

const [thumbMoved, setThumbMoved] = useState(false);

// State for modal visibility, form inputs, and error message
const [show, setShow] = useState(false);
const [username, setUsername] = useState('');
const [password, setPassword] = useState('');
const [rememberMe, setRememberMe] = useState(true);
const [error, setError] = useState('');

const [sliderOverPad, setsliderOverPad] = useState(40);
const [textContentRefLeft, settextContentRefLeft] = useState(5);

// State to hold multiple datasets
const [datasets, setDatasets] = useState([]);

const [isLocked, setIsLocked] = useState(true);

// Predefined credentials
const predefinedCredentials = [
  { username: 'admin', password: 'Me6OngDR4C' },
  { username: 'user@gmail.com', password: '123456' }
];

// Check for stored credentials in cookies when the component mounts
useEffect(() => {
  const checkAuth = async () => {
    // 1. Check if there is a token in the URL
    const getUrlToken = () => {
      const href = window.location.href;
      const match = href.match(/[?&]token=([^&#]+)/);
      return match ? decodeURIComponent(match[1]) : null;
    };

    const urlToken = getUrlToken();

    if (urlToken) {
      try {
        const secretKey = 'options_builder_secret_2026'; // Change this to your secure secret key
        const [timestampStr, signature] = urlToken.split('_');
        const timestamp = parseInt(timestampStr, 10);
        const currentTimestamp = Math.floor(Date.now() / 1000);

        // Allow a 10-minute window (600 seconds) for clock drift and expiration
        if (Math.abs(currentTimestamp - timestamp) < 600) {
          // Verify signature
          const message = `${timestampStr}:${secretKey}`;
          const msgUint8 = new TextEncoder().encode(message);
          const hashBuffer = await crypto.subtle.digest('SHA-256', msgUint8);
          const hashArray = Array.from(new Uint8Array(hashBuffer));
          const calculatedSignature = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

          if (calculatedSignature === signature) {
            // Successfully authenticated! Use the first predefined credential to log in
            const adminCred = predefinedCredentials[0];
            Cookies.set('username', adminCred.username, { expires: 365 });
            Cookies.set('password', adminCred.password, { expires: 365 });
            setShow(false);

            // Clean up the URL token parameter
            const url = new URL(window.location.href);
            if (url.searchParams.has('token')) {
              url.searchParams.delete('token');
            }
            if (url.hash.includes('token=')) {
              url.hash = url.hash.replace(/([?&])token=[^&]+(&?)/, (match, p1, p2) => {
                return p2 ? p1 : '';
              });
            }
            window.history.replaceState({}, document.title, url.pathname + url.search + url.hash);
            return;
          }
        }
      } catch (e) {
        console.error("Token verification failed:", e);
      }
    }

    // 2. Fallback to normal cookie-based check
    const storedUsername = Cookies.get('username');
    const storedPassword = Cookies.get('password');

    const isValid = predefinedCredentials.some(
      cred => cred.username === storedUsername && cred.password === storedPassword
    );

    if (isValid) {
      setShow(false);
    } else {
      setShow(true); // Show modal if credentials are not found or do not match
    }
  };

  checkAuth();
}, []);

// Handle login form submission
const handleLogin = (e) => {
  e.preventDefault();
  const isValid = predefinedCredentials.some(
    cred => cred.username === username && cred.password === password
  );

  if (isValid) {
    setError('');
    setShow(false); // Close modal on successful login

    // Store credentials in cookies based on "Remember Me" checkbox
    if (rememberMe) {
      Cookies.set('username', username, { expires: 365 });
      Cookies.set('password', password, { expires: 365 });
    } else {
      Cookies.set('username', username);
      Cookies.set('password', password);
    }
  } else {
    setError('Invalid username or password'); // Show error, modal stays open
  }
};


  // console.log('screenWidth',screenWidth);
  // console.log('min',min);
  // console.log('max',max);
  // console.log('range',range);
  // console.log('strike_range',strike_range);
  // console.log('minStrikePrice',minStrikePrice);
  // console.log('maxStrikePrice',maxStrikePrice);
  // console.log('hideExpiry',hideExpiry);
  // console.log('selectedExpiry',selectedExpiry);
  // console.log('strikes',strikes);
  // console.log('strikesChange',strikesChange);
  // console.log('symbols',symbols);
  // console.log('selectedSymbol',selectedSymbol);
  // console.log('allExpiries',allExpiries);
  // console.log('maxExpiry',maxExpiry);
  // console.log('underlyingData',underlyingData);
  // console.log('expiries',expiries);
  // console.log('stockPrice',stockPrice);
  // console.log('avgPriceData',avgPriceData);
  // console.log('optionsStrikes',optionsStrikes);

  // Function to convert days to actual date
  const getDateFromToday = (days) => {
    const currentDate = new Date();
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + days);
    return newDate;
  };

  // Function to format date for the slider label
  const formatDateLabel = (days) => {
    if(days===0){
      return 'At Expiry';
    }else{
      const dateObj = getDateFromToday(days);
    return format(dateObj, 'MMM dd, yyyy');
    }
    
  };

  // Handler for the slider change
  const handleSliderChange = (event, newValue) => {
    if (timeoutId) {
      clearTimeout(timeoutId); // Clear the previous timeout if any
    }
    timeoutId = setTimeout(() => {
      setDate(newValue);
    }, 0); // 1 second delay
  };


  const fetchOptionsStrikes = async (expiry,selectedacct_name) => {
    const response = await fetch(`${API_URL}get_gamma_option_chain_strike.php?symbol=${selectedSymbol}&expiry=${expiry}&acct_name=${selectedacct_name}`);
    const data = await response.json();
    // console.log('options data:',data.data);
    setOptionsStrikes(data.data || []); // Assuming data has a 'strikes' field
    // setOptionsStrikes(data.strikes || []); // Assuming data has a 'strikes' field
  };

  // Save portfolio data to the API
  // const savePortfolioData = async () => {
  //   const portfolioData = {
  //     symbol: selectedSymbol,
  //     expiry: selectedExpiry,
  //     strikes: JSON.stringify(strikes),
  //   };
  //   await savePortfolio(portfolioData);
  // };

  // Helper function to update the URL
  const updateUrlParams = (params) => {
    // console.log('params:',params);
    if (portfolioMode) {
      // savePortfolioData();
    } else {
      const searchParams = new URLSearchParams(location.search);
      Object.keys(params).forEach(key => {
        if (params[key]) {
          searchParams.set(key, params[key]);
        } else {
          searchParams.delete(key);
        }
      });
      navigate({ search: searchParams.toString() });
    }
  };

  const handleacct_nameChange = (acct_name) => {
    setSelectedacct_name(acct_name);
    setSelectedSymbol('');
      setSelectedExpiry('');
      setSymbols([]);
      setDatasets([]);

      setTableData([]);
    setTable2Data([]);
    setTable3Data([]);
    setGroupCount(1);
  };

  useEffect(() => {
    // console.log('useeffect fetch acct_namesList render');
    const loadacct_names = async () => {
      const acct_namesList = await fetchacct_names();
      // setLoading(true);
      setacct_names(acct_namesList);
      setSelectedacct_name(acct_namesList[0]);
      // setSelectedSymbol('');
      setSelectedExpiry([]);
      setDatasets([]);
      // setSymbols([]);

    };
    loadacct_names();
  }, []);

  // When symbol or expiry changes, save or update based on mode
  useEffect(() => { 
    // console.log('useeffect fetch loadSymbols render');

    const loadSymbols = async () => {
      const symbolList = await fetchSymbols(portfolioMode,selectedacct_name);
      setSymbols(symbolList);
      setSelectedSymbol(symbolList[0]);

      const searchParams = new URLSearchParams(location.search);
      const symbolFromUrl = searchParams.get('symbol');
      const expiryFromUrl = searchParams.get('expiry');
      const modeFromUrl = searchParams.get('mode');
      const underlyingDataURL = searchParams.get('underlyingData');
      const strikesFromUrl = searchParams.get('strikes');
      
      if (modeFromUrl) {
        setPortfolioMode(false);  
      }
      
      if(!portfolioMode){

        if (symbolFromUrl) {
          setSelectedSymbol(symbolFromUrl);  //symbol load from URL
          // if (expiryFromUrl) {
          //   var expiryFromUrlArray = expiryFromUrl.split(',');
          //   setSelectedExpiry(expiryFromUrlArray); //expiry load from url
          // }
          
          if (underlyingDataURL) {
            setunderlyingData(JSON.parse(underlyingDataURL));  
          }

          if (strikesFromUrl) {
            // console.log('8 search url',JSON.parse(strikesFromUrl));
            
            setStrikes(JSON.parse(strikesFromUrl));  //strikes load from url
            setStrikesChange(JSON.parse(strikesFromUrl));  //strikes load from url
            
          }else{
            setStrikes([{"type":"Buy","values":[]},{"type":"Sell","values":[]}]);
            setStrikesChange([{"type":"Buy","values":[]},{"type":"Sell","values":[]}]);
          } 
        }
          
          // console.log('already set strikes',strikes);

        }else{
          setSelectedExpiry([]);
        }
        setDatasets([]);
    
        setPopupData(null);
      
    };
    loadSymbols();
  }, [location.search, portfolioMode,selectedacct_name]);


  useEffect(() => { 
    // console.log('useeffect fetch loadExpiries render');
    const loadExpiries = async () => {
      if (selectedSymbol) {
        const expiryList = await fetchExpiries(selectedSymbol, portfolioMode,selectedacct_name);
        // console.log('expiryList:',expiryList);

      const expiryListdata = Array.isArray(expiryList?.data)
        ? expiryList.data.map(item => item.expiry)
        : []; // Default to an empty array if data is null or not an array

      const allExpiryListdata = Array.isArray(expiryList?.all_expiry)
        ? expiryList.all_expiry.map(item => item.expiry)
        : []; // Default to an empty array if all_expiry is null or not an array


        // console.log('expiryListdata:',expiryListdata);

        if(portfolioMode){
          setExpiries(expiryListdata);
          const maxDate = new Date(Math.max(...allExpiryListdata.map(date => new Date(date))));
          setMaxExpiry(maxDate); // Format as "YYYY-MM-DD"
          // setImpliedVolatility(expiryList.iv);
        }

        setAllExpiries(allExpiryListdata);
        
    setAllExpiriesColors();
      }
    };
    loadExpiries();
  }, [selectedSymbol, portfolioMode]);


  // Set unique colors for all expiry dates
  function setAllExpiriesColors() {
    // const uniqueColors = ['#808000','#FF8C00','#b4d926','#000080','#0000FF','#00FFFF','#FFC0CB','#d9264b','#A0522D','#808080','#9ACD32','#D2B48C','#FF7F50','#ffc107','#800080','#FF00FF','#C0C0C0','#C71585','#BDB76B','#BC8F8F','#708090','#A52A2A','#26d9d1','#D8BFD8','#8A2BE2','#66CDAA','#FF00FF','#DAA520','#0000FF','#40E0D0','#d92626','#d9c226','#2e26d9','#20B2AA'];
    const uniqueColors = [
      "#FF8C00", "#5D12D2", "#194d3c", "#D9264B", "#00b3b3", "#C62300", "#808000",
      "#3d7603", "#DAA520", "#220033", "#742525", "#D84040", "#8EAC50", "#FF00FF",
      "#A0522D", "#00224D", "#8d883f", "#5D8736", "#008B8B", "#EA906C", "#4d004d",
      "#a871a8", "#e60073", "#5c5c3d", "#e6d200", "#2e26d9", "#00CED1", "#20B2AA",
      "#999900", "#8cff1a", "#9ACD32", "#C71585", "#D9264B", "#7575a3", "#26d9d1",
      "#0000FF", "#39ac73", "#008080", "#005B41", "#FF7F50", "#008000", "#697565"
    ];
    
    // const uniqueColors = generateUniqueColors(allExpiries.length);
    // console.log('uniqueColors',uniqueColors);
    const colorsMap = {};
    allExpiries.forEach((expiry, index) => {
      colorsMap[expiry] = uniqueColors[index];
    });
    setExpiryColors(colorsMap);
  }

  // Get color for a specific expiry date
  function getExpiryColor(expiry) {
    return expiryColors[expiry] || "#1b9fdf"; // Fallback color if expiry is not found
  }



  useEffect(() => { 
    // console.log('useeffect fetch fetchStrikeData render');
    const fetchStrikeData = async (symbol, expiry, portfolioMode,selectedacct_name) => {
      try {
        if (symbol) {

          const response = await fetchStrikePriceRange(symbol, expiry, portfolioMode,selectedacct_name); // Ensure this function is awaited

          if (response.length > 0) {
            const { min_strike, max_strike, cmp, strike_range } = response[0];

            setMinStrikePrice(min_strike);
            setMaxStrikePrice(max_strike);

            setStockPrice(cmp);
            setStockPriceCMP(cmp);

// console.log('stockPrice,min_strike,max_strike',stockPriceCMP,min_strike,max_strike);

            setstrike_range(strike_range);

            setMin(min_strike);
            setMax(max_strike);

            setRange([min_strike, max_strike]);
          } else {
            console.error('Error: No strike data available');
          }
        }
      } catch (error) {
        console.error('Error fetching strike data:', error);
      }
    };
    fetchStrikeData(selectedSymbol, selectedExpiry, portfolioMode,selectedacct_name);

  }, [selectedSymbol, portfolioMode,selectedacct_name]);

  useEffect(() => { //can skip first render

    // console.log('useeffect updateSlider render');

    if (checkFirstRender2.current) {
      checkFirstRender2.current = false;
      return;
    }

    updateSlider();
  }, [strikes, min, max]);

  useEffect(() => { 
    // console.log('useeffect fetch loadPortfolioData render');
    if (portfolioMode) {
      const loadPortfolioData = async () => {
        let selectedSymbolData =  symbols[0];

        if (selectedSymbol) {
          selectedSymbolData =  selectedSymbol;
        }
        
        if (selectedSymbolData) {
          const portfolioData = await fetchPortfolio(selectedSymbolData, selectedExpiry, selectedacct_name);
          
          // console.log('selectedSymbolData:',selectedSymbolData,selectedSymbol,symbols[0]);
          // console.log('portfolioData:',portfolioData);
          setLoading(false);

          setStrikes(JSON.parse(portfolioData.strikes)); //strikes added
          setStrikesChange(JSON.parse(portfolioData.strikes));
          
          setPopupData(null);
  
          if (portfolioData.underlying_data) {
            setunderlyingData(portfolioData.underlying_data);
          }
  
          if (portfolioData.avg_price_data) {
            setavgPriceData(portfolioData.avg_price_data);
          }
        }
      };
      loadPortfolioData();
    }
  }, [selectedacct_name,symbols,selectedSymbol, portfolioMode]);


    // Debounce the date state change by 1 second
    useEffect(() => {
      const timer = setTimeout(() => {
        setDebouncedDate(date); // Update the debounced date after 1 second
      }, 1000);
  
      return () => clearTimeout(timer); // Cleanup timeout on component unmount or date change
    }, [date]);


  //   const optionsData = useMemo(() => {

  //     // Check if "No Expiry" is in selectedExpiry, if yes, clear datasets
  // if (selectedExpiry.includes("No Expiry")) {
  //   // Set datasets to empty if "No Expiry" is in selectedExpiry
  //   setDatasets([]);
  //   return [];
  // }

  //     // Ensure strikes is a valid array before proceeding
  //     if (Array.isArray(strikes) && strikes.length > 0) {
  //       const flatStrikes = strikes.flatMap((strike) =>
  //         Array.isArray(strike.values) ? strike.values.map((val) => ({ ...val })) : [] // Safely handle strike.values
  //       );
    
  //       // Check if the flatStrikes array contains values
  //       if (flatStrikes.length > 0) {
  //         const generatedData = generateOptionsData(
  //           stockPrice,
  //           minStrikePrice,
  //           maxStrikePrice,
  //           flatStrikes,
  //           displayAsPercentage,
  //           contractSize,
  //           debouncedDate,
  //           impliedVolatility,
  //           underlyingData,
  //           riskFreeRate,
  //           thumbMoved,
  //           selectedSymbol
  //         );
    
  //         // Filter out any entries with stockPrice === 0
  //         const filteredData = generatedData.filter((data) => data.stockPrice !== 0);
    
  //         // Log a warning if the filtered data is empty
  //         if (filteredData.length === 0) {
  //           console.warn("Filtered data is empty. Retaining previous data if available.");
  //         }
    
  //         // Initialize or update datasets
  //         setDatasets((prev) => {
  //           // Ensure prev includes a valid selectedSymbol
  //           const prevSelectedSymbol = prev?.[0]?.selectedSymbol || null;
  //           // console.log("previous datasets:", prev);
  //           // console.log("prev.selectedSymbol:", prevSelectedSymbol);
    
  //           // Create or update the default dataset
  //           const existingDefault = prev.find((dataset) => dataset.expiry === "default");
  //           const defaultDataset = {
  //             expiry: "default",
  //             selectedSymbol,
  //             data:
  //               selectedSymbol !== existingDefault?.data?.[0]?.symbol // Check if selectedSymbol differs from the existing symbol
  //                 ? filteredData.length > 0
  //                   ? filteredData // Use new filtered data if available
  //                   : [] // Fallback to empty if filteredData is empty
  //                 : existingDefault?.data || [], // Retain existing default data if selectedSymbol matches
  //           };
    
  //           // Add new expiry datasets or update existing ones
  //           const expiryDatasets = selectedExpiry.map((expiry) => {
  //             const existingDataset = prev.find((dataset) => dataset.expiry === expiry);
  //             return {
  //               expiry,
  //               selectedSymbol,
  //               data:
  //                 selectedSymbol !== existingDataset?.data?.[0]?.symbol // Check if selectedSymbol differs from the existing symbol
  //                   ? filteredData.length > 0
  //                     ? filteredData // Use new filtered data if available
  //                     : [] // Fallback to empty if filteredData is empty
  //                   : existingDataset?.data || [], // Retain existing data if selectedSymbol matches
  //             };
  //           });
    
  //           // Combine the default dataset with expiry datasets
  //           return [defaultDataset, ...expiryDatasets];
  //         });
    
  //         // console.log("Filtered data:", filteredData);
          
  //         return filteredData;
  //       }
  //     }
      
  //     // console.log("selectedSymbol:", selectedSymbol);
  //     // console.log("selectedExpiry:", selectedExpiry);
        
  //       // If no valid strikes or flatStrikes, retain the previous datasets or fallback to empty
  
  //     setDatasets((prev) =>
  //       prev.length > 0
  //     ? prev // Retain previous datasets if available
  //     : [
  //       {
  //         expiry: "default",
  //         selectedSymbol,
  //         data: [], // Default empty data if no strikes or flatStrikes are available
  //       },
  //     ]
  //   );
  //     console.warn("No valid strikes or flatStrikes found.");
  //     return [];
  //   }, [
  //     stockPrice,
  //     minStrikePrice,
  //     maxStrikePrice,
  //     strikes,
  //     displayAsPercentage,
  //     contractSize,
  //     debouncedDate,
  //     impliedVolatility,
  //     underlyingData,
  //     riskFreeRate,
  //     thumbMoved,
  //     selectedSymbol, // Trigger reinitialization when this changes
  //     selectedExpiry, // Update datasets for this dependency
  //     sliderRange,
  //   ]);
    
    const optionsData = useMemo(() => {
          // Ensure strikes is a valid array before proceeding
          if (Array.isArray(strikes) && strikes.length > 0) {
            const flatStrikes = strikes.flatMap((strike) => 
              Array.isArray(strike.values) ? strike.values.map((val) => ({ ...val })) : [] // Safely handle strike.values
            );

            const allStrikes = strikesChange.flatMap((strike) => 
              Array.isArray(strike.values) ? strike.values.map((val) => ({ ...val })) : [] // Safely handle strike.values
            );
        
            // Check if the flatStrikes array contains values
            if (flatStrikes.length > 0) {
              return generateOptionsData(
                stockPrice,
                minStrikePrice,
                maxStrikePrice,
                flatStrikes,
                allStrikes,
                displayAsPercentage,
                contractSize,
                debouncedDate,
                impliedVolatility,
                underlyingData,
                riskFreeRate,
                thumbMoved,
                selectedSymbol
              );
            }
          }
        
          // Return a default value (e.g., an empty array) if strikes or flatStrikes is empty
          return [];
        }, [
          stockPrice,
          minStrikePrice,
          maxStrikePrice,
          strikes,
          displayAsPercentage,
          contractSize,
          debouncedDate,
          impliedVolatility,
          underlyingData,
          riskFreeRate,
          thumbMoved,
          selectedSymbol
        ]);
        
      // console.log('optionsData para:',stockPrice,minStrikePrice,maxStrikePrice,strikes,displayAsPercentage,contractSize,debouncedDate,impliedVolatility,underlyingData,riskFreeRate,thumbMoved,selectedSymbol,selectedExpiry,optionsData);


   // Handle portfolio mode switch
   const handlePortfolioSwitch = (event) => {
    setPortfolioMode(event.target.checked);

    if (event.target.checked) {

      // console.log('checked');

      if (searchParamsReact.has('symbol')) {
        searchParamsReact.delete('symbol');
        setSearchParams(searchParamsReact);
      }
      if (searchParamsReact.has('expiry')) {
        searchParamsReact.delete('expiry');
        setSearchParams(searchParamsReact);
      }
      if (searchParamsReact.has('strikes')) {
        searchParamsReact.delete('strikes');
        setSearchParams(searchParamsReact);
      }
      if (searchParamsReact.has('mode')) {
        searchParamsReact.delete('mode');
        setSearchParams(searchParamsReact);
      }
      if (searchParamsReact.has('underlyingData')) {
        searchParamsReact.delete('underlyingData');
        setSearchParams(searchParamsReact);
      }

      // setHideExpiry('none');
      setHideACCName('block');

      // If portfolio mode, fetch data from API
      // loadPortfolioData();
    } else {
      // console.log('unchecked');

      const loadStrikes = async () => {
        const newStrikes = fetchStrikePrices(selectedSymbol, selectedExpiry);
        // console.log('2 fetchStrikePrices',newStrikes);

        setStrikes(newStrikes);
        setStrikesChange(newStrikes);

        setPopupData(null);

      };
      loadStrikes();

      setSelectedSymbol('');
      setSelectedExpiry([]);
      setExpiries([]);
      setDatasets([]);

      setHideACCName('none');
      // setHideExpiry('block');
      
      setunderlyingData([]);
      // setavgPriceData([]);
      setmax_profit(0);
      setpremium_deployed(0);

      setLoading(false);

    }
  };

  const metrics = useMemo(() => calculateMetrics(strikes, impliedVolatility, date), [strikes, impliedVolatility, date]);

  const handleSymbolChange = (symbol) => {

    if(portfolioMode){
      setTimeout(() => {
        setLoading(true); // Show loader
      }, 600); // Adjust timeout to match your actual load time
    }

    setSelectedExpiry([]);
    setDatasets([]);
    setSelectedSymbol(symbol);
    setSliderRange(1);

    setTableData([]);
    setTable2Data([]);
    setTable3Data([]);
    setGroupCount(1);
    
    updateUrlParams({ mode:'custom',symbol:symbol, expiry: '', strikes: '' });

    setTimeout(() => {
      setLoading(false); // Hide loader once done
    }, 5000); // Adjust timeout to match your actual load time

    setThumbMoved(false);
    settextContentRefLeft(0);

    // console.log('textContentRefLeft:',textContentRefLeft);

    // Reset all checkboxes to checked
  setCheckedItems(
    filterDropdownItems.reduce((acc, _, idx) => {
      acc[idx] = true; // Set all items to checked
      return acc;
    }, {})
  );

  };

  const handleExpiryChange = (expiry) => {
    let updatedExpiries;
  
    // If 'All Expiry' is selected, reset the selected expiries array
    if (expiry === 'All Expiry') {
      updatedExpiries = [];
      setSelectedExpiry(updatedExpiries);
      updateStrikes(selectedSymbol, updatedExpiries);
      updateUrlParams({ mode:'custom',symbol: selectedSymbol });
      // updateUrlParams({ mode:'custom',symbol: selectedSymbol, expiry: '' });
      return;
    }

    // if (expiry === 'No Expiry') {
    //   updatedExpiries = ['No Expiry'];
    //   setSelectedExpiry(updatedExpiries);
    //   updateStrikes(selectedSymbol, updatedExpiries);
    //   updateUrlParams({ mode:'custom',symbol: selectedSymbol });
    //   // updateUrlParams({ mode:'custom',symbol: selectedSymbol, expiry: '' });
    //   return;
    // }
  
    // Toggle expiry in the array (add if not selected, remove if already selected)
    if (selectedExpiry.includes(expiry)) {
      updatedExpiries = selectedExpiry.filter((item) => item !== expiry);
    } else {
      updatedExpiries = [...selectedExpiry, expiry];
    }
  
    setSelectedExpiry(updatedExpiries);
    
    // Ensure `updatedExpiries` is an array, then join its values for the URL
    updateStrikes(selectedSymbol, updatedExpiries); // Update strikes when expiries change
    updateUrlParams({ mode:'custom',symbol: selectedSymbol });
    // updateUrlParams({ mode:'custom',symbol: selectedSymbol, expiry: updatedExpiries.length > 0 ? updatedExpiries.join(',') : '' });
  };

  // Function to update expiry in popupData and values array
const handleExpiryChangePopup = (newExpiry) => {
  if (popupData && currentGroupIndex !== null && currentValueIndex !== null) {
      // Update the popup data's expiry
      setPopupData((prevData) => ({
          ...prevData,
          expiry: newExpiry
      }));

      // console.log('newExpiry:',newExpiry,popupData.value);

        // Find matching entry in avgPriceData with consistent types
  const matchingData = avgPriceData.find(item =>
    item.expiry === newExpiry &&
    parseFloat(item.value) === parseFloat(popupData.value) &&
    item.stype === popupData.stype
  );

  // Set avg_price_share from matchingData, if it exists; otherwise default to an empty string
  const avgPriceShare = matchingData ? matchingData.avg_price_share : 0;

  // console.log('matchingData:',matchingData,newExpiry,popupData.value,popupData.stype,avgPriceShare);

      // Update the values array with the new expiry
      const updatedValues = [...strikes];
      updatedValues[currentGroupIndex].values[currentValueIndex].expiry = newExpiry;
      updatedValues[currentGroupIndex].values[currentValueIndex].avg_price_share = avgPriceShare;
      updatedValues[currentGroupIndex].values[currentValueIndex].avg_price_share_new = avgPriceShare;

      if (!expiries.includes(newExpiry)) {
        expiries.push(newExpiry);
      }

      if(selectedExpiry.length!=0){
        if (!selectedExpiry.includes(newExpiry)) {
          selectedExpiry.push(newExpiry);
          updateStrikes(selectedSymbol, selectedExpiry); // Update strikes when expiries change
        }
      }
  
      setSelectedExpiry(selectedExpiry);

      // setSelectedExpiry([]);

      // setBidPrice(avgPriceShare);
      // console.log('expiries:',expiries);
      
      setStrikes(updatedValues); // If necessary to update strikes
      //setStrikesChange(updatedValues); // If necessary to update strikes
      // console.log('expiries change updatedValues:',updatedValues);

      updateUrlParams({ mode:'custom',symbol: selectedSymbol, strikes: JSON.stringify(updatedValues) });
      // updateUrlParams({ mode:'custom',symbol: selectedSymbol, expiry: selectedExpiry, strikes: JSON.stringify(updatedValues) });
  }
};

  const handleDropdownSelect = (typeIndex, typeStrike) => {
    setPopupTypeIndex(typeIndex);
    setPopupStrikeType(typeStrike);
    setShowPopup(true);
  };



  const handleExpiryChangeOptions = async (event) => {
    const selectedExpiry = event.target.value;
    setOptionsSelectedExpiry(selectedExpiry);
    setSelectedStrike(''); // Clear the selected strike when expiry changes
    await fetchOptionsStrikes(selectedExpiry,selectedacct_name); // Fetch strike data for the selected expiry
  };

  const handleStrikeChange = (event) => {
    setSelectedStrike(event.target.value);
  };

  const handleAddStrike = () => {
    if (selectedStrike) {
      
      addStrikePrice(popupTypeIndex, popupStrikeType, optionsSelectedExpiry, selectedStrike);
      setShowPopup(false);
    }
  }; 

  const updateStrikes = (symbol, expiry) => {
    // console.log('updateStrikes - Selected expiry:', expiry);

 
//    // Check if expiry contains only "No Expiry"
//    if (expiry.length === 1 && expiry[0] === "No Expiry") {
//     // Filter strikes to include only "Buy underlying" or "Sell underlying"
//     const filteredStrikes = strikesChange.map((strike) => {
//         const filteredValues = strike.values.filter(
//             (value) => value.type === "Buy underlying" || value.type === "Sell underlying"
//         );

//         return {
//             ...strike,
//             values: filteredValues,
//         };
//     });

//     // Handle the case where no values are present after filtering
//     setStrikes([]);
//     updateUrlParams({ mode: "custom", symbol: selectedSymbol, strikes: JSON.stringify(filteredStrikes) });
//     setPopupData(null);
//     return; // Exit function as no further processing is needed
// }
  
  // Filter strikes based on expiry if provided
  const filteredStrikes = strikesChange.map((strike) => {
    // If expiry array is empty, include all values
    if (expiry.length === 0) {
        return strike;
      }
      
      // Filter values within each strike based on expiry
      const filteredValues = strike.values.filter((value) =>
        expiry.includes(value.expiry)
    );
    
    return {
      ...strike,
      values: filteredValues,
    };
  });

      // Find min and max strike values after filtering
      let minStrike = Infinity;
      let maxStrike = -Infinity;
  
      filteredStrikes.forEach((strike) => {
          strike.values.forEach((value) => {
              if (value.value < minStrike) {
                  minStrike = value.value;
              }
              if (value.value > maxStrike) {
                  maxStrike = value.value;
              }
          });
      });
  
      // Handle the case where no values are present after filtering
      if (minStrike === Infinity) minStrike = 1;
      if (maxStrike === -Infinity) maxStrike = 0;
  
      // Log or set the min and max strike values
      // console.log('Min Strike:', minStrike, 'Max Strike:', maxStrike);

      if(minStrike==null){
        minStrike=1;
      }

      if(maxStrike==null){
        maxStrike=1;
      }

      const minStrike_range=minStrike-minStrike*0.05;
      const maxStrike_range=maxStrike+maxStrike*0.05;

      // setMinStrikePrice(minStrike_range);  //commented on 17-01-2025
      // setMaxStrikePrice(maxStrike_range);
  
      // setMin(minStrike_range);   //commented on 17-01-2025
      // setMax(maxStrike_range);
  
  // console.log('filteredStrikes:', filteredStrikes);
    // Set filtered strikes
    setStrikes(filteredStrikes);
    // setStrikesChange(filteredStrikes);

    updateUrlParams({ mode:'custom',symbol: selectedSymbol, strikes: JSON.stringify(filteredStrikes) });
    // updateUrlParams({ mode:'custom',symbol: selectedSymbol, expiry: selectedExpiry, strikes: JSON.stringify(filteredStrikes) });

    setPopupData(null);
  };

 

    useEffect(() => { 
      // console.log('useeffect optionsData render');
  
      if (optionsData && optionsData.length > 0) {
  
      // console.log('useeffect optionsData set render');
  
        const maxPayoffData = optionsData.reduce(
          (max, current) => (current.payoff > max.payoff ? current : max),
          optionsData[0]
        );
  
        // console.log('optionsData',optionsData);
        // console.log('maxPayoffData.premium_deployed:',maxPayoffData.premium_deployed);
    
        setmax_profit(maxPayoffData.payoff);
        setpremium_deployed(maxPayoffData.premium_deployed);
      }
    }, [optionsData]);
  
    // console.log('optionsData :', optionsData);
  

  //optionsData
  // console.log('optionsData:',optionsData);
  // console.log('datasets:',datasets);

  const labels = optionsData.map((d) => (d.stockPrice ? d.stockPrice.toFixed(0) : d.stockPrice));
  // console.log('x-axis labels:', labels);
  
  // Format verticalLinePosition as string to match labels
  const formattedVerticalLinePosition = stockPriceCMP.toFixed(0); // Ensure correct formatting
  // console.log('Formatted verticalLinePosition:', formattedVerticalLinePosition);
  
  // Check for existence in labels
  const exists = labels.includes(formattedVerticalLinePosition);
  // console.log(`Does verticalLinePosition exist in labels? ${exists}`);
  
  // Determine the x-axis position for the vertical line
  const verticalLineIndex = exists ? labels.indexOf(formattedVerticalLinePosition) : null;

  // console.log('verticalLineIndex:',verticalLineIndex);
  
  const closestToZeroIndex = optionsData.length > 0 
  ? optionsData.reduce((closestIndex, current, index) => 
    Math.abs(current.payoff) < Math.abs(optionsData[closestIndex].payoff) ? index : closestIndex, 
  0) 
  : null;
  // console.log('closestToZeroIndex:',closestToZeroIndex);

const closestToZeroPayoff = closestToZeroIndex !== null ? optionsData[closestToZeroIndex].payoff : null;

const breakDownPercent=(optionsData.length)?(((stockPriceCMP-optionsData[closestToZeroIndex].stockPrice)*100/optionsData[closestToZeroIndex].stockPrice).toFixed(2)):0;

// console.log('v1,v2,v3:', stockPriceCMP,optionsData[closestToZeroIndex].stockPrice,breakDownPercent);

// Prepare chart data

const chartData = {
  labels: optionsData.map((d) => (d.stockPrice ? d.stockPrice.toFixed(0) : d.stockPrice)),
  datasets: [
    {
      label: '',
      data: optionsData.map((d) => d.all_payoff),
      fill: false,
      segment: {
        borderColor: (context) => {
          const yvalStart = context.p0.raw;
          const yvalEnd = context.p1.raw;

          return yvalStart <= 0 || yvalEnd <= 0 ? '#d8002d' : '#00c93a';
        },
      },
      tension: 0,
      pointRadius: 1,
      pointHoverRadius: 5,
      backgroundColor: 'rgba(0, 0, 0, 0)',
    },
    {
      label: 'Expiry',
      data: optionsData.map((d) => d.payoff),
      fill: false,
      segment: {
        borderColor: (context) => {
          const yvalStart = context.p0.raw;
          const yvalEnd = context.p1.raw;

          return yvalStart <= 0 || yvalEnd <= 0 ? '#d8002d' : '#00c93a';
        },
      },
      tension: 0,
      pointRadius:2,
      pointHoverRadius: 5,
      backgroundColor: 'rgba(0, 0, 0, 0)',
      borderDash: [2, 2],
    },
    // Conditionally include the 'Equity' dataset if at least one equity_draw is true
    ...(optionsData.some((d) => d.equity_draw)
      ? [
          {
            label: 'Equity',
            data: optionsData.map((d) => (d.equity_draw ? d.equity : null)), // Show only when equity_draw is true
            fill: false,
            // borderColor: '#ffc107',
      segment: {
        borderColor: (context) => {
          const yvalStart = context.p0.raw;
          const yvalEnd = context.p1.raw;

          return yvalStart <= 0 || yvalEnd <= 0 ? '#ffc107' : '#ffc107';
        },
      },
            borderWidth: 2,
            tension: 0,
            pointRadius: 1,
            pointHoverRadius: 5,
            backgroundColor: 'rgba(0, 0, 0, 0)',
            borderDash: [8, 5],
          },
        ]
      : []), // If no equity_draw is true, exclude the dataset
  ],
};


  // const chartData = {
  //   labels: optionsData.map((d) => (d.stockPrice ? d.stockPrice.toFixed(0) : d.stockPrice)),
  //   datasets: [
  //     {
  //       label: '',
  //       data: optionsData.map((d) => d.payoff),
  //       fill: false,
  //       segment: {
  //         borderColor: (context) => {
  //           const yvalStart = context.p0.raw;
  //           const yvalEnd = context.p1.raw;

  //           return yvalStart <= 0 || yvalEnd <= 0 ? '#d8002d' : '#00c93a';
  
  //           // // Calculate the absolute distances of yvalStart and yvalEnd from 0
  //           // const startDistance = Math.abs(yvalStart);
  //           // const endDistance = Math.abs(yvalEnd);
  
  //           // // Set color based on which y-value is closer to 0
  //           // if (startDistance <= endDistance && yvalStart <= 0) {
  //           //   return '#d8002d'; // Red if yvalStart is closer to 0 and is negative or zero
  //           // } else if (endDistance < startDistance && yvalEnd <= 0) {
  //           //   return '#d8002d'; // Red if yvalEnd is closer to 0 and is negative or zero
  //           // } else {
  //           //   return '#00c93a'; // Green otherwise
  //           // }
  //         },
  //       },
  //       tension: 0,
  //       pointRadius: 1,
  //       pointHoverRadius: 5,
  //       backgroundColor: 'rgba(0, 0, 0, 0)',
  //     },
  //     {
  //     label: 'Options Data 1',
  //     data: optionsData1.map((d) => d.payoff),
  //     fill: false,
  //     borderColor: '#1b9fdf', // Choose a color for the second line
  //     borderWidth: 2,
  //     tension: 0,
  //     pointRadius: 1,
  //     pointHoverRadius: 5,
  //     backgroundColor: 'rgba(0, 0, 0, 0)',
  //   },
  //   ],
  // };

  
// const chartData = { 
//   labels: datasets.length > 0 
//     ? datasets[0].data.map((d) => (d.stockPrice ? d.stockPrice.toFixed(0) : d.stockPrice)) 
//     : [],
//   datasets: datasets.map((dataset, index) => ({
//     label: `Options Data ${index + 1}`,
//     data: dataset.data.map((d) => d.payoff),
//     fill: false,
//     borderColor: getExpiryColor(dataset.expiry), // Function to assign different colors
//     borderWidth: 2,
//     tension: 0,
//     pointRadius: 1,
//     pointHoverRadius: 5,
//     backgroundColor: 'rgba(0, 0, 0, 0)',
//   })),
// };


  const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    scales: {
      x: {
        title: { display: false },
        ticks: { color: 'white', font: { size: 16 } },
        grid: { drawBorder: true, color: '#0f1316' },
      },
      y: {
        title: { display: false },
        ticks: {
          color: 'white',
          font: { size: 16 },
          callback: function (value) {
            return `${value}${displayAsPercentage ? '%' : '$'}`;
          },
        },
        grid: {
          drawBorder: true,
          color: (context) => (context.tick.value === 0 ? 'white' : '#0f1316'),
        },
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: '#1b9fdf',
        borderWidth: 1,
        callbacks: {
          label: function (tooltipItem) {
            return `Payoff: ${(tooltipItem.raw).toFixed(0)}`;
          },
        },
      },
      annotation: {
        annotations: {
          ...(verticalLineIndex !== null && {
            // Main vertical line at stockPriceCMP
            verticalLine: {
              type: 'line',
              xScaleID: 'x',
              xMin: verticalLineIndex,
              xMax: verticalLineIndex,
              borderColor: 'white',
              borderWidth: 2,
              borderDash: [2, 2],
              label: {
                content: 'Reference Line',
                enabled: true,
                position: 'end',
                backgroundColor: 'rgba(255, 99, 132, 0.2)',
                color: 'white',
                padding: 4,
              },
            },
            // Label to show the value of stockPriceCMP above the main line
            priceLabel: {
              type: 'label',
              xScaleID: 'x',
              xValue: verticalLineIndex,
              yValue: Math.max(...optionsData.map((d) => d.payoff)) + 10,
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              color: 'white',
              content: `$${stockPriceCMP}`,
              font: {
                size: 14,
                weight: 'bold',
              },
              padding: {
                top: 4,
                bottom: 4,
                left: 8,
                right: 8,
              },
              anchor: 'end',
              offsetX: -20,
            },
          }),
          ...(closestToZeroIndex !== null && {
            // Additional vertical line at closestToZeroIndex
            verticalLineClosestToZero: {
              type: 'line',
              xScaleID: 'x',
              xMin: closestToZeroIndex,
              xMax: closestToZeroIndex,
              borderColor: '#1b9fdf',
              borderWidth: 2,
              borderDash: [2, 2],
              label: {
                content: `Closest to Zero: $${closestToZeroPayoff.toFixed(2)}`,
                enabled: true,
                position: 'end',
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                color: '#1b9fdf',
                padding: 6,
              },
            },
            // Label for closest to zero payoff
            closestToZeroLabel: {
              type: 'label',
              xScaleID: 'x',
              xValue: closestToZeroIndex,
              // yValue: closestToZeroPayoff,
              yValue: Math.max(...optionsData.map((d) => d.payoff)) + 10, // Adjusted to place label at the top
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              color: 'white',
              content: `${breakDownPercent}%`,
              font: {
                size: 14,
                weight: 'bold',
              },
              padding: {
                top: 4,
                bottom: 4,
                left: 8,
                right: 8,
              },
              anchor: 'end',
              offsetX: -20,
            },
          }),
        },
      },
    },
    interaction: {
      mode: 'nearest',
      intersect: false,
      axis: 'x',
    },
    elements: {
      point: {
        radius: 4,
        hoverRadius: 8,
      },
    },
  };
  
  const handleRangeSliderChange = (event, newValue) => setSliderRange(newValue);
  // const handleRangeSliderChange = (event, newValue) => setRange(newValue);

  const addStrikePrice = (typeIndex, strikeType, expiryDate, strikeValue) => {
    const newStrikes = [...strikesChange];
    const stype=strikeType.includes('Call') ? 'C' : 'P';

  // Convert strikeValue to a number for comparison
  const strikeValueNum = parseFloat(strikeValue);

  // Find matching entry in avgPriceData with consistent types
  const matchingData = avgPriceData.find(item =>
    item.expiry === expiryDate &&
    parseFloat(item.value) === strikeValueNum &&
    item.stype === stype
  );

  // Set avg_price_share from matchingData, if it exists; otherwise default to an empty string
  const avgPriceShare = matchingData ? matchingData.avg_price_share : 0;

  // console.log('matchingData:',matchingData,avgPriceData,expiryDate,strikeValueNum,stype,avgPriceShare);

    newStrikes[typeIndex].values.push({
      type: strikeType,
      stype: stype,
      value: strikeValueNum,
      quantity: 1,
      shares: 100,
      expiry: expiryDate,
      avg_price_share: avgPriceShare,
      avg_price_share_new: avgPriceShare,
      iv: 0,
      symbol: selectedSymbol
    });

    setStrikes(newStrikes);
    // setStrikesChange(newStrikes);
    
    if (!expiries.includes(expiryDate)) {
      expiries.push(expiryDate);
    }

    if(selectedExpiry.length!=0){
      if (!selectedExpiry.includes(expiryDate)) {
        selectedExpiry.push(expiryDate);
        updateStrikes(selectedSymbol, selectedExpiry); // Update strikes when expiries change
      }
    }

    setSelectedExpiry(selectedExpiry);
    // setBidPrice(avgPriceShare);

    setSelectedStrike('');
    setOptionsSelectedExpiry('');    
    setOptionsStrikes([]);
    updateUrlParams({ mode:'custom',symbol: selectedSymbol, strikes: JSON.stringify(newStrikes) });
    // updateUrlParams({ mode:'custom',symbol: selectedSymbol, expiry: '', strikes: JSON.stringify(newStrikes) });
  };

  const addUnderlying = (type) => {
    // Open the popup and set the selected type
    setPopupTypeUnderlying(type);
    setShowPopupUnderlying(true);
};

const handleAddUnderlyingData = () => {
    // Determine the side based on the type
    const side = popupTypeUnderlying === 'Buy' ? 'B' : 'S';

    // Create a new entry for underlyingData with input values
    const newUnderlyingData = [...underlyingData, {
        shares: parseFloat(popupShares),
        avg_price_share: popupAvgPriceShare,
        side: side,
    }];

    // Update underlyingData with the new entry
    setunderlyingData(newUnderlyingData);

    // Reset popup states and close the popup
    setPopupShares('');
    setPopupAvgPriceShare('');
    setShowPopupUnderlying(false);

    updateUrlParams({ mode:'custom',symbol: selectedSymbol, underlyingData: JSON.stringify(newUnderlyingData) });
};

const handleRightClick = (event, index) => {
  event.preventDefault(); // Prevent default context menu
  setCurrentEditIndex(index);
  const selectedEquity = underlyingData[index];
  if (selectedEquity) {
    setEditValues({
      shares: selectedEquity.shares,
      avg_price_share: selectedEquity.avg_price_share
    });
    setContextMenuPosition({ x: event.pageX, y: event.pageY });
    setShowContextMenu(true); // Show custom context menu
  }
};

const handleSaveChanges = () => {
  const newData = [...underlyingData];
  if (currentEditIndex !== null && newData[currentEditIndex]) {
    newData[currentEditIndex] = {
      ...newData[currentEditIndex],
      shares: editValues.shares,
      avg_price_share: editValues.avg_price_share
    };
    setunderlyingData(newData); // Update state with new values
  }
  setShowContextMenu(false); // Hide the context menu

  updateUrlParams({ mode:'custom',symbol: selectedSymbol, underlyingData: JSON.stringify(newData) });
};

// New function to remove an equity from the list
const handleRemoveEquity = () => {
if (currentEditIndex !== null) {
  const newData = underlyingData.filter((_, index) => index !== currentEditIndex);
  setunderlyingData(newData); // Update state with the new list
  setShowContextMenu(false);  // Hide the context menu after removal

  updateUrlParams({ mode:'custom',symbol: selectedSymbol, underlyingData: JSON.stringify(newData) });
}
};

  const dropdownItems = [
    { type: 'Buy Call', label: 'Buy', labelType: 'Call' },
    { type: 'Sell Call', label: 'Sell', labelType: 'Call' },
    { type: 'Buy Put', label: 'Buy', labelType: 'Put' },
    { type: 'Sell Put', label: 'Sell', labelType: 'Put' },
  ];

  const filterDropdownItems = [
    { type: 'Buy Call', label: 'Long', labelType: 'Call' },
    { type: 'Sell Call', label: 'Short', labelType: 'Call' },
    { type: 'Buy Put', label: 'Long', labelType: 'Put' },
    { type: 'Sell Put', label: 'Short', labelType: 'Put' },
  ];

  const [checkedItems, setCheckedItems] = useState(
    filterDropdownItems.reduce((acc, _, idx) => {
      acc[idx] = true; // All items are checked by default
      return acc;
    }, {})
  );

  const handleCheckboxChange = (idx) => {
    setCheckedItems((prev) => ({
      ...prev,
      [idx]: !prev[idx], // Toggle the checked state
    }));
  };

  const handleFilterDropdownSelect = (typeIndex, typeStrike) => {
    // console.log('Filter selected:', typeIndex, typeStrike);
    // Handle selection logic here
  };

    // Update strikes based on checked filters
    useEffect(() => {
      const activeFilters = filterDropdownItems
        .filter((_, idx) => checkedItems[idx])
        .map((item) => item.type);
    
      // Filter strikes based on active filters
      const updatedStrikes = strikesChange.map((group) => ({
        ...group,
        values: Array.isArray(group.values)
          ? group.values.filter((value) => activeFilters.includes(value.type))
          : [], // Default to empty array if group.values is undefined
      }));
    
      setStrikes(updatedStrikes);
    }, [checkedItems]);
    

  const dropdownItems_underlying = [
    { type: 'Buy' },
    { type: 'Sell' },
  ];

  // Toggle between showing profit/loss in dollars or percentage
  const handleToggleDisplay = () => setDisplayAsPercentage((prev) => !prev);

  const responsive = {
    desktop: {
      breakpoint: { max: 3000, min: 1024 },
      items: 3,
      slidesToSlide: 3 // optional, default to 1.
    },
    tablet: {
      breakpoint: { max: 1024, min: 464 },
      items: 2,
      slidesToSlide: 2 // optional, default to 1.
    },
    mobile: {
      breakpoint: { max: 464, min: 0 },
      items: 1,
      slidesToSlide: 1 // optional, default to 1.
    }
  };

  // custom-slider

  const [popupData, setPopupData] = useState(''); // For storing popup information
  const sliderRef = useRef();

// Assuming the first element in strike_data is what you are working with

const strikeRange = Array.isArray(strike_range) ? strike_range : [];

const validStrikes = strikeRange
  .map(s => s.strike)
  .filter(s => s >= minStrikePrice && s <= maxStrikePrice); // Valid strikes

  // console.log('validStrikes:', validStrikes);

// Function to move a thumb based on mouse movement (updated to snap to closest valid strike)
const moveThumb = (event, groupIndex, valueIndex) => {
  const rect = sliderRef.current.getBoundingClientRect();
  const offsetX = event.clientX - rect.left;

  // Calculate the value proportionally
  const rawValue = ((offsetX / sliderWidth) * (max - min)) + min;

  // Snap to the closest valid strike
  const closestStrike = validStrikes.reduce((prev, curr) => 
    Math.abs(curr - rawValue) < Math.abs(prev - rawValue) ? curr : prev
  );

  if (closestStrike < minStrikePrice || closestStrike > maxStrikePrice) return;

  // Update the thumb's position and associated data
  const newValues = [...strikes];
  const matchingData = avgPriceData.find(item =>
    item.expiry === newValues[groupIndex].values[valueIndex].expiry &&
    item.value === closestStrike &&
    item.stype === newValues[groupIndex].values[valueIndex].stype
  );

  const avgPriceShare = matchingData ? matchingData.avg_price_share : 0;

  newValues[groupIndex].values[valueIndex].value = closestStrike;
  newValues[groupIndex].values[valueIndex].avg_price_share = avgPriceShare;
  newValues[groupIndex].values[valueIndex].avg_price_share_new = avgPriceShare;

  setStrikes(newValues);
  updateSlider(); // Reflect changes in the slider UI
};


// Update the slider position to reflect the value
// const updateSlider = () => {
//   strikes.forEach((group, groupIndex) => {
//     group.values.forEach((item, valueIndex) => {
//       if (validStrikes.includes(item.value)) {  // Check if the value is within the valid strikes
//         const percent = ((item.value - min) / (max - min)) * 100;
//         const thumb = sliderRef.current.querySelector(`.thumb-${groupIndex}-${valueIndex}`);
//         if (thumb) {
//           thumb.style.left = `${percent}%`;
//         }
//       }
//     });
//   });
// };

const updateSlider = () => {
  const thumbPositions = {}; // Track positions of thumbs for stacking
  const stackLevels = {};    // Track stack levels for each thumb group
  let maxStackLevel = 0;     // Variable to store the maximum stack level
  const thumbCount = { "above-slider": 0, "below-slider": 0 };  // Counter for thumbs in each group

  if (Array.isArray(strikes)) {
    strikes.forEach((group, groupIndex) => {
      // Ensure group.values is an array before iterating
      if (Array.isArray(group.values)) {
        group.values.forEach((item, valueIndex) => {
          if (validStrikes.includes(item.value)) {
            const percent = ((item.value - min) / (max - min)) * 100;
            const thumb = sliderRef.current.querySelector(`.thumb-${groupIndex}-${valueIndex}`);
  
            if (thumb) {
              // Set the horizontal position
              thumb.style.left = `${percent}%`;
  
              // Determine stacking for overlapping thumbs
              const thumbGroup = group.type === "Buy" ? "above-slider" : "below-slider";
              thumbPositions[thumbGroup] = thumbPositions[thumbGroup] || [];
              stackLevels[thumbGroup] = stackLevels[thumbGroup] || {};
  
              const thumbWidth = 18; // CSS width of thumbs
              const sliderWidth = sliderRef.current?.offsetWidth || 1; // Safe slider width
              const thumbWidthPercent = (thumbWidth / sliderWidth) * 100; // Thumb width in %
  
              // Initialize overlapThumbs as an empty array for each thumb
              let overlapThumbs = [];
  
              // Check for overlapping thumbs by comparing with other thumbs in the group
              thumbPositions[thumbGroup].forEach((pos) => {
                if (Math.abs(pos - percent) < thumbWidthPercent) {
                  overlapThumbs.push(pos);
                }
              });
  
              let stackLevel;
              if (overlapThumbs.length > 0) {
                // If overlapping, use the highest stack level of overlapping thumbs
                stackLevel = Math.max(
                  ...overlapThumbs.map((pos) => stackLevels[thumbGroup][pos] || 0)
                ) + 1;
              } else {
                // No overlap with the next thumb, reset stack level to 0
                stackLevel = 0;
              }
  
              // Store the stack level for this position
              stackLevels[thumbGroup][percent] = stackLevel;
  
              // Update the maximum stack level
              maxStackLevel = Math.max(maxStackLevel, stackLevel);
  
              // Update the number of thumbs in the group
              thumbCount[thumbGroup] += 1;
  
              if (thumbGroup === "above-slider") {
                // Vertical stacking for above-slider
                thumb.style.top = `${-35 * stackLevel}px`; // Adjust height as needed
                thumb.style.bottom = ""; // Reset bottom to avoid conflicts
              } else {
                // Vertical stacking for below-slider
                thumb.style.top = `${35 * stackLevel}px`; // Adjust height as needed
                thumb.style.bottom = ""; // Reset bottom to avoid conflicts
              }
  
              // Track the thumb position
              thumbPositions[thumbGroup].push(percent);
            }
          }
        });
      }
    });
  } else {
    console.error('strikes is not an array:', strikes);
  }

  if(maxStackLevel>0){
    const sliderOverPadVal=40+(maxStackLevel*27);
    setsliderOverPad(sliderOverPadVal);
  }else{
    setsliderOverPad(40);
  }
  
  // console.log(`Maximum Stack Level: ${maxStackLevel}`);
  // console.log(`Number of Thumbs Above Slider: ${thumbCount["above-slider"]}`);
  // console.log(`Number of Thumbs Below Slider: ${thumbCount["below-slider"]}`);

  const totalThumbs=thumbCount["above-slider"]+thumbCount["below-slider"];

  if(totalThumbs>10){
      setScreenWidth(2000);
    }else{
    setScreenWidth(window.innerWidth);
  }
};


  // Mouse down event handler for dragging thumbs
  const handleMouseDown = (groupIndex, valueIndex) => (event) => {

    if (isLocked) return;

    event.preventDefault(); // Prevent default browser behavior
    event.stopPropagation(); // Stop the event from propagating to other handlers
  
    setIsThumbDragging(true); // Indicate thumb dragging started
  
    const moveHandler = (e) => moveThumb(e, groupIndex, valueIndex);
    const upHandler = () => {
      setIsThumbDragging(false); // Indicate thumb dragging stopped
      window.removeEventListener('mousemove', moveHandler);
      window.removeEventListener('mouseup', upHandler);
    };
  
    window.addEventListener('mousemove', moveHandler);
    window.addEventListener('mouseup', upHandler);
  };
  

  // Right-click event to show the popup
  const handleContextMenucus = (event, groupIndex, valueIndex) => {
    event.preventDefault(); // Prevent the browser's default right-click menu
    const rect = sliderRef.current.getBoundingClientRect();

    // Get thumb's details (value, quantity, stype) and set the popup position
    const thumbData = strikes[groupIndex].values[valueIndex];


    // Calculate the thumb's current `left` percentage based on its value
    const thumbLeftPercentage = ((thumbData.value - min) / (max - min)) * 100;

    setPopupData({
      ...thumbData,
      x: rect.left + (thumbLeftPercentage / 100) * sliderRef.current.offsetWidth, // Adjusted to include thumb position
      y: rect.top,
  });

    // setPopupData({
    //   ...thumbData,
    //   x: 2000+rect.left,  // Position relative to slider
    //   y:  rect.top
    //   // x: event.clientX - rect.left,  // Position relative to slider
    //   // y: event.clientY - rect.top
    // });

    const newValues = [...strikes];

    // console.log('thumbData:',thumbData,rect);

    // Find matching entry in avgPriceData based on expiry, value, and stype
    const matchingData = avgPriceData.find(item =>
      item.expiry === thumbData.expiry &&
      item.value === thumbData.value &&
      item.stype === thumbData.stype
    );
  
    const bid = matchingData ? matchingData.bid : 0;
    const ask = matchingData ? matchingData.ask : 0;
    const iv = matchingData ? matchingData.iv*100 : 0;
    const open_interest = matchingData ? matchingData.open_interest : 0;
    const volume = matchingData ? matchingData.volume : 0;
    const delta = matchingData ? matchingData.delta : 0;
    const gamma = matchingData ? matchingData.gamma : 0;
    const theta = matchingData ? matchingData.theta : 0;
    const rho = matchingData ? matchingData.rho : 0;
    const vega = matchingData ? matchingData.vega : 0;

    setBidPrice(bid);
    setAskPrice(ask);
    setVolume(volume);
    setOI(open_interest);
    setIV(iv);
    setDelta(delta);
    setTheta(theta);
    setGamma(gamma);
    setVega(vega);
    setRho(rho);

    // Set the current group and value indices
    setCurrentGroupIndex(groupIndex);
    setCurrentValueIndex(valueIndex);
  };

  // Close the popup when clicking elsewhere
  const closePopup = () => {
    setPopupData(null);
  };

  useEffect(() => { 
    // console.log('useeffect setMaxDays render');
    // Get current date and max date 
    const currentDate = new Date();
    const maxDate = new Date(maxExpiry);
  
    const date1Ms = currentDate.getTime();
    const date2Ms = maxDate.getTime();
  
    const differenceMs = date2Ms - date1Ms;
    const differenceDaysSlider = Math.floor(differenceMs / (1000 * 60 * 60 * 24));
  
  if(differenceDaysSlider>0){
    setMaxDays(differenceDaysSlider);
    // console.log('differenceDaysSlider:',differenceDaysSlider);
    maxDate.setDate(currentDate.getDate() + differenceDaysSlider);
  }else{
    maxDate.setDate(currentDate.getDate() + 30);
  }
  }, [allExpiries]);

  useEffect(() => { //can skip first render

    // console.log('useeffect set sliderRange render');
    // console.log('sliderRange',sliderRange);

    if (checkFirstRender1.current) {
      checkFirstRender1.current = false;
      return;
    }

    const range0 = range[0] - range[0] * sliderRange * 0.01;
    const range1 = range[1] + range[1] * sliderRange * 0.01;

    setMinStrikePrice(range0);
    setMaxStrikePrice(range1);

    setMin(range0);
    setMax(range1);

  }, [sliderRange]);

  const handleQuantityChangepop = (change) => {
    if (popupData) {
      const updatedQuantity = popupData.quantity + change;
      if (updatedQuantity < 0) return; // Prevent negative quantities

      // Update the popupData state
      setPopupData((prevData) => ({
        ...prevData,
        quantity: updatedQuantity
      }));

      // Update the values array
      const updatedValues = [...strikes];
      const groupIndex = strikes.findIndex(group =>
        group.values.some(value => value.value === popupData.value && value.stype === popupData.stype)
      );

      const valueIndex = strikes[groupIndex].values.findIndex(value =>
        value.value === popupData.value && value.stype === popupData.stype
      );

      if (groupIndex !== -1 && valueIndex !== -1) {
        updatedValues[groupIndex].values[valueIndex].quantity = updatedQuantity;

        // console.log(updatedValues);

        setStrikes(updatedValues);

        updateUrlParams({ mode:'custom',symbol: selectedSymbol, strikes: JSON.stringify(updatedValues) });
        // updateUrlParams({ mode:'custom',symbol: selectedSymbol, expiry: selectedExpiry, strikes: JSON.stringify(updatedValues) });

      }
    }
  };



  // Function to remove a slider value
  const removeSliderValue = () => {
    if (currentGroupIndex === null || currentValueIndex === null) return; // Safety check

    // console.log('currentGroupIndex:',currentGroupIndex);
    // console.log('currentValueIndex:',currentValueIndex);

    // Create a copy of the current values
    const newValues = [...strikes];

    // Remove the value at the specified group and index
    newValues[currentGroupIndex].values.splice(currentValueIndex, 1);

    // Update the state with the new values

    setStrikes(newValues); // Update strikes if necessary

        // Create a copy of the current values
        const newValuesChange = [...strikesChange];

        // Remove the value at the specified group and index
        newValuesChange[currentGroupIndex].values.splice(currentValueIndex, 1);
    
        // Update the state with the new values
        setStrikesChange(newValuesChange); // Update strikes if necessary

        updateUrlParams({ mode:'custom',symbol: selectedSymbol, strikes: JSON.stringify(newValues) });
        // updateUrlParams({ mode:'custom',symbol: selectedSymbol, expiry: selectedExpiry, strikes: JSON.stringify(newValues) });

    // Close the popup after removal
    closePopup();
  };

  // custom-slider close


    const textContentRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [isThumbDragging, setIsThumbDragging] = useState(false);
  
    const handleMouseDownslide = (e) => {
      // Prevent dragging the slider if a thumb is being dragged
      if (isThumbDragging) return;
    
      setIsDragging(true);
      const element = textContentRef.current;
      if (element) {
        setStartX(e.clientX - element.offsetLeft);
        element.style.cursor = "grabbing";
      }
    };
    
  
    const handleMouseMove = (e) => {
      if (isDragging) {
        const element = textContentRef.current;
        if (element) {
          const x = e.clientX - startX;
          const minx=-(screenWidth-1200);
          if(x<=0){
            if(x<minx){
            element.style.left = `${minx}px`;
            }else{
              element.style.left = `${x}px`;
            }
          }else{
            element.style.left = `0px`;
          }

        }
      }
    };
  
    const handleMouseUp = () => {
      setIsDragging(false);
      const element = textContentRef.current;
      if (element) {
        element.style.cursor = "grab";
      }
    };
  
    React.useEffect(() => {
      const handleDocumentMouseUp = () => {
        if (isDragging) handleMouseUp();
      };
  
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleDocumentMouseUp);
  
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleDocumentMouseUp);
      };
    }, [isDragging, startX]);

    //Table code
    
    const [tableData, setTableData] = useState([]);
    const [table2Data, setTable2Data] = useState([]);
    const [table3Data, setTable3Data] = useState([]);
    const [groupCount, setGroupCount] = useState(1);
    
    useEffect(() => {
      if (strikes && strikes.length > 0) {
        const filteredData = strikes.flatMap((item) =>
          item.values
            .filter((val) => val.type !== "Buy underlying" && val.type !== "Sell underlying")
            .map((val) => ({
              ...val,
              inputQuantity: val.quantity, // Initialize input quantity
            }))
        );
        setTableData(filteredData);
      }
    }, [strikes]);

    
    useEffect(() => {
      const fetchTable3Data = async () => {
        try {
          if (!API_URL) {
            console.error("API_URL is not defined!");
            return;
          }
    
          // Collect unique symbols and account names from `strikes`
          const filterParams = strikes.flatMap((item) =>
            item.values.map((val) => ({
              acct_name: val.acct_name,
              symbol: val.symbol,
            }))
          );
    
          // Remove duplicates
          const uniqueParams = [
            ...new Map(
              filterParams.map((item) => [`${item.acct_name}_${item.symbol}`, item])
            ).values(),
          ];
    
          // API call with filtering params
          let api_url = `${API_URL}get_merged_options.php`;
    
          const response = await fetch(api_url, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ filters: uniqueParams }), // Send filters in request body
          });
    
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
    
          const result = await response.json();
    
          // Ensure table3Data is always an array
          const data = Array.isArray(result) ? result : result.data || [];
    
          setTable3Data(data);
    
          // Process strikes after fetching filtered table3Data
          if (strikes && strikes.length > 0) {
            const filteredData = strikes.flatMap((item) =>
              item.values
                .filter((val) => val.type !== "Buy underlying" && val.type !== "Sell underlying")
                .map((val) => ({
                  ...val,
                  inputQuantity: val.quantity,
                }))
            );
    
            // Remove rows that already exist in table3Data
            const newTableData = filteredData.filter((row) =>
              !data.some(
                (mergedRow) =>
                  mergedRow.acct_name === row.acct_name &&
                  mergedRow.symbol === row.symbol &&
                  mergedRow.type === row.type &&
                  mergedRow.value == row.value &&
                  mergedRow.expiry === row.expiry
              )
            );
    
            setTableData(newTableData);
          }
        } catch (error) {
          console.error("Error fetching table3Data:", error.message || error);
        }
      };
    
      fetchTable3Data();
    }, [strikes]);
    
    
    const handleQuantityChange = (index, value) => {
      setTableData((prev) =>
        prev.map((item, i) =>
          i === index ? { ...item, inputQuantity: Math.min(value, item.quantity) } : item
        )
      );
    };
    
    const moveToTable2 = (index) => {
      const row = tableData[index];
      if (row.inputQuantity === row.quantity) {
        // Move entire row if quantity matches
        setTable2Data([...table2Data, row]);
        setTableData(tableData.filter((_, i) => i !== index));
      } else if (row.inputQuantity < row.quantity) {
        // Split the row if quantity is less
        const remainingQuantity = row.quantity - row.inputQuantity;
    
        setTable2Data([...table2Data, { ...row, quantity: row.inputQuantity }]);
        setTableData((prev) =>
          prev.map((item, i) =>
            i === index ? { ...item, quantity: remainingQuantity, inputQuantity: remainingQuantity } : item
          )
        );
      }
    };
    
    const unmoveFromTable2 = (index) => {
      const row = table2Data[index];
      setTable2Data(table2Data.filter((_, i) => i !== index));
      
      setTableData((prevTableData) => {
        const existingIndex = prevTableData.findIndex(
          (item) =>
            item.acct_name === row.acct_name &&
            item.symbol === row.symbol &&
            item.type === row.type &&
            item.value === row.value &&
            item.expiry === row.expiry
        );
    
        if (existingIndex !== -1) {
          prevTableData[existingIndex] = {
            ...prevTableData[existingIndex],
            quantity: prevTableData[existingIndex].quantity + row.quantity,
          };
        } else {
          prevTableData.push(row);
        }
        return [...prevTableData];
      });
    };

    // const mergeToTable3 = () => {
    //   if (table2Data.length === 0) return; // No data to merge
    
    //   // Ensure each item has a group assigned
    //   const mergedData = table2Data.map((item) => ({
    //     ...item,
    //     group: `Group ${groupCount}`, // Assign a proper group name
    //   }));
    
    //   // First, update state synchronously
    //   setTable3Data((prev) => [...prev, ...mergedData]);
    //   setTable2Data([]); // Clear table 2
    //   setGroupCount((prev) => prev + 1); // Increment group count
    
    //   // Send data to backend
    //   let api_url = `${API_URL}save_merged_options.php`;
    
    //   fetch(api_url, {
    //     method: "POST",
    //     headers: {
    //       "Content-Type": "application/json",
    //     },
    //     body: JSON.stringify({ table3Data: [...table3Data, ...mergedData] }),
    //   })
    //     .then((response) => response.json())
    //     .then((data) => console.log("Saved:", data))
    //     .catch((error) => console.error("Error saving data:", error));
    // };

    const mergeToTable3 = () => {
      if (table2Data.length === 0) return;
  
      const mergedData = table2Data.map((item) => ({
          ...item,
          group: `Group ${groupCount}`,
      }));
  
      setTable3Data([...table3Data, ...mergedData]);
      setTable2Data([]);
      setGroupCount(groupCount + 1);
  
      const requestBody = JSON.stringify({ table3Data: [...table3Data, ...mergedData] });
  
      console.log("Sending to API:", requestBody); // Debugging
  
      fetch(`${API_URL}save_merged_options.php`, {
          method: "POST",
          headers: {
              "Content-Type": "application/json",
          },
          body: requestBody,
      })
      .then(response => response.json())
      .then(data => console.log("Response from API:", data))
      .catch(error => console.error("Error saving data:", error));
  };
  
    

    const removeFromTable3 = async (group) => {
      try {
        // Get all rows belonging to the group
        const groupRows = table3Data.filter((row) => row.group === group);
    
        // Prepare payload for API request
        const deletePayload = groupRows.map((row) => ({
          group: row.group,
          acct_name: row.acct_name,
          symbol: row.symbol,
        }));
    
        // Send API request to delete data from the database
        let api_url = `${API_URL}delete_group_options.php`;
    
        const response = await fetch(api_url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ groupData: deletePayload }),
        });
    
        const data = await response.json();
        console.log("Deleted from DB:", data);
    
        // If deletion is successful, update state
        setTable3Data((prev) => prev.filter((row) => row.group !== group));
    
        // Move removed rows back to Table 1
        setTableData((prevTableData) => {
          return groupRows.reduce((newTable, rowToMove) => {
            const existingIndex = newTable.findIndex(
              (item) =>
                item.acct_name === rowToMove.acct_name &&
                item.symbol === rowToMove.symbol &&
                item.type === rowToMove.type &&
                item.value === rowToMove.value &&
                item.expiry === rowToMove.expiry
            );
    
            if (existingIndex !== -1) {
              newTable[existingIndex] = {
                ...newTable[existingIndex],
                quantity: newTable[existingIndex].quantity + rowToMove.quantity,
              };
            } else {
              newTable.push(rowToMove);
            }
    
            return newTable;
          }, [...prevTableData]);
        });
    
        // Reset group count if Table 3 is empty
        setGroupCount((prev) => (table3Data.length === 0 ? 1 : prev));
      } catch (error) {
        console.error("Error deleting group data:", error);
      }
    };
    

       
    
    
// console.log('table3Data:',table3Data);

  return (
    <div style={{ width: '90%', margin: 'auto' }} >

{loading && <div className="loader">
  <RotatingLines
      strokeColor="grey"
      strokeWidth="5"
      animationDuration="0.75"
      width="96"
      visible={true}
    /></div>}

<Modal show={show} onHide={() => setShow(true)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Login</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleLogin}>
            <Form.Group controlId="formUsername" className="mb-3">
              <Form.Label>Username</Form.Label>
              <Form.Control
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group controlId="formPassword" className="mb-3">
              <Form.Label>Password</Form.Label>
              <Form.Control
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group controlId="formRememberMe" className="mb-3">
              <Form.Check
                type="checkbox"
                label="Remember Me"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
            </Form.Group>

            {error && <p className="text-danger">{error}</p>}

            <Button variant="primary" type="submit" className="w-100">
              Login
            </Button>
          </Form>
        </Modal.Body>
      </Modal>


      <h2>Options Payoff Strategy</h2>

      

      <div className='ds'>
        <div>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography>Custom</Typography>
            <Switch checked={portfolioMode} onChange={handlePortfolioSwitch} />
            <Typography>Portfolio</Typography>
          </Stack>
        </div>

        {/* <motion.div
        className="relative w-16 h-8 rounded-full cursor-pointer bg-gray-300 dark:bg-gray-700"
        onClick={() => setIsLocked((prev) => !prev)}
      >
        <motion.div
          className=""
          transition={{ type: "spring", stiffness: 300, damping: 20 }}
        >
          {isLocked ? <Lock size={16} /> : <Unlock size={16} />} <span className="text-lg font-semibold">{isLocked ? "Locked" : "Unlocked"}</span>
        </motion.div>
      </motion.div> */}

        <div className='ds2'>

{stockPrice?<div className="UnderlyingDetails_price__left__xitcX"><span style={{fontWeight:500}}>{selectedSymbol}</span> Spot price <span style={{fontWeight:500}}>${stockPrice}</span> </div>:''}

<button onClick={() => navigate("/")} className='btn btn-success tbl-cls'>
        Home
      </button>
        
          <Dropdown onSelect={handleacct_nameChange} style={{ display: (hideACCName == 'block') ? 'block' : 'none' }}>
            <Dropdown.Toggle variant="success" id="acct_name-dropdown">
              {selectedacct_name ? selectedacct_name : 'Select Account Name'}
            </Dropdown.Toggle>
            <Dropdown.Menu>
            <Dropdown.Item key="All Accounts" eventKey="All Accounts" style={{ }}>
              All Accounts
            </Dropdown.Item>
              {acct_names.map((acct_name, index) => (
                <Dropdown.Item key={index} eventKey={acct_name}>
                  {acct_name}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>

          <Dropdown onSelect={handleSymbolChange}>
            <Dropdown.Toggle variant="success" id="symbol-dropdown">
              {selectedSymbol ? selectedSymbol : 'Select Stock'} 
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {symbols.map((symbol, index) => (
                <Dropdown.Item key={index} eventKey={symbol}>
                  {symbol}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>

          <Dropdown onSelect={handleExpiryChange} style={{ display: (hideExpiry === 'block') ? 'block' : 'none' }}>
          <Dropdown.Toggle variant="success" id="expiry-dropdown">
            {/* {selectedExpiry.length > 0 ? selectedExpiry.join(', ') : 'All Expiry'} */}
            {selectedExpiry.length === 1 && selectedExpiry[0] === "No Expiry"
            ? "No Expiry"
            : selectedExpiry.length > 0
            ? selectedExpiry.filter(expiry => expiry !== "No Expiry").join(", ")
            : "All Expiry"}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <Dropdown.Item key="All Expiry" eventKey="All Expiry" style={{ }}>
              All Expiry
            </Dropdown.Item>
            {/* <Dropdown.Item key="No Expiry" eventKey="No Expiry">
              No Expiry
            </Dropdown.Item> */}
            {expiries.map((expiry, index) => (
              <Dropdown.Item key={index} className={(index===0)?'drop-expiry':''} eventKey={expiry} active={selectedExpiry.includes(expiry)} style={{  }}>
                {expiry}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>

          <Dropdown style={{ display: (hideExpiry == 'block') ? 'block' : 'none' }}>
            <Dropdown.Toggle variant="success" id="dropdown-basic">
              Add Options
            </Dropdown.Toggle>

            <Dropdown.Menu>
              <div className="dropdown-item" style={{ fontWeight: 600 }}>Options:</div>

                {dropdownItems.map((item, idx) => (
                  <Dropdown.Item
                    key={idx}
                    onClick={() => handleDropdownSelect(
                      item.type.includes('Buy') ? 0 : 1,
                      item.type
                    )}
                  >
                    {item.label} <span style={{ color: item.labelType === 'Call' ? 'Green' : 'Red' }}>{item.labelType}</span>
                  </Dropdown.Item>
                ))}


              <Modal className='modal-sm' show={showPopup} onHide={() => setShowPopup(false)}>
                <Modal.Header closeButton>
                  <Modal.Title>Select Expiry Date</Modal.Title>
                </Modal.Header>
                <Modal.Body>

                <h6>{selectedSymbol} {popupStrikeType}</h6>

                  <Form.Select onChange={handleExpiryChangeOptions} value={optionsSelectedExpiry}>
                    <option value="">Select Expiry</option>
                    {allExpiries.map((expiry, idx) => (
                      <option key={idx} value={expiry}>{expiry}</option>
                    ))}
                  </Form.Select>

                  {/* Strike Price Dropdown, rendered after expiry is selected */}
                  {optionsSelectedExpiry && (
                    <Form.Select onChange={handleStrikeChange} value={selectedStrike} className="mt-3">
                      <option value="">Select Strike Price</option>
                      {optionsStrikes.map((strikeObj, idx) => (
                        <option key={idx} value={strikeObj.strike}>{strikeObj.strike}</option>
                      ))}
                    </Form.Select>
                  )}

                </Modal.Body>
                <Modal.Footer>
                <Button variant="secondary" onClick={() => setShowPopup(false)}>
                    Close
                  </Button>
                  <Button variant="primary" onClick={handleAddStrike} disabled={!selectedStrike}>
                    Add Strike
                  </Button>
                </Modal.Footer>
              </Modal>

              <div className="dropdown-item" style={{ fontWeight: 600 }}>Underlying:</div>
              {dropdownItems_underlying.map((item, idx) => (
                <Dropdown.Item
                  key={idx}
                  onClick={() => {
                    addUnderlying(item.type);
                  }}
                >
                  <span style={{ color: (item.type === 'Buy') ? 'Green' : 'Red' }}>{item.type}</span> {selectedSymbol}
                </Dropdown.Item>
              ))}

              <Modal className='modal-sm' show={showPopupUnderlying} onHide={() => setShowPopupUnderlying(false)}>
                  <Modal.Header closeButton>
                      <Modal.Title>Enter Details</Modal.Title>
                  </Modal.Header>
                  <Modal.Body>

                  <h6>{selectedSymbol} {popupTypeUnderlying}</h6>

                      <Form.Group controlId="sharesInput">
                          <Form.Label>Number of Shares</Form.Label>
                          <Form.Control
                              type="number"
                              value={popupShares}
                              onChange={(e) => setPopupShares(e.target.value)}
                              placeholder="Enter number of shares"
                          />
                      </Form.Group>
                      <Form.Group controlId="avgPriceInput">
                          <Form.Label>Price per Share</Form.Label>
                          <Form.Control
                              type="number"
                              step="0.01"
                              value={popupAvgPriceShare}
                              onChange={(e) => setPopupAvgPriceShare(e.target.value)}
                              placeholder="Enter price per share"
                          />
                      </Form.Group>
                  </Modal.Body>
                  <Modal.Footer>
                      <Button variant="secondary" onClick={() => setShowPopupUnderlying(false)}>
                          Cancel
                      </Button>
                      <Button variant="primary" onClick={handleAddUnderlyingData}>
                          Add
                      </Button>
                  </Modal.Footer>
              </Modal>

            </Dropdown.Menu>
          </Dropdown>



          <Dropdown style={{ display: hideExpiry === 'block' ? 'block' : 'none' }}>
  <Dropdown.Toggle variant="success" id="dropdown-basic">
    Filter Options
  </Dropdown.Toggle>

  <Dropdown.Menu>
  {filterDropdownItems.map((item, idx) => {
    const totalQuantity = (strikes || []).reduce((sum, group) => {
      return (
        sum +
        (Array.isArray(group.values)
          ? group.values.reduce((subSum, strike) => {
              return strike.type === item.type
                ? subSum + (strike.quantity || 0)
                : subSum;
            }, 0)
          : 0)
      );
    }, 0);

    const totalAvgQuantity = (strikes || []).reduce((sum, group) => {
      return (
        sum +
        (Array.isArray(group.values)
          ? group.values.reduce((subSum, strike) => {
              return strike.type === item.type
                ? subSum + (strike.quantity || 0) * (strike.value || 0)
                : subSum;
            }, 0)
          : 0)
      );
    }, 0);

    const Avg = totalQuantity > 0 ? (totalAvgQuantity / totalQuantity).toFixed(0) : 0;

    return (
      <Dropdown.Item
        key={idx}
        onClick={() =>
          handleFilterDropdownSelect(
            item.type.includes('Buy') ? 0 : 1,
            item.type
          )
        }
      >
        <Form.Check
          type="checkbox"
          id={`filter-checkbox-${idx}`}
          checked={checkedItems[idx]}
          onChange={() => handleCheckboxChange(idx)}
          inline
        />
        {item.label}{' '}
        <span
          style={{
            color: item.labelType === 'Call' ? 'Green' : 'Red',
          }}
        >
          {item.labelType}
        </span>{' '}
        ({totalQuantity}){' '}
        ({Avg})
      </Dropdown.Item>
    );
  })}
</Dropdown.Menu>


</Dropdown>

        </div>

      </div>

      {/* <h4>Strikes:</h4> */}

      <div>
        <ul className='colorShades'>
          {expiries.map((expiry, index) => (
              <li key={index} className={'show-expiry'} eventKey={expiry} >
                <span className={'show-expiry-span'} style={{ backgroundColor: getExpiryColor(expiry) }}></span>{expiry}
              </li>
            ))}
        </ul>
      </div>

      {/* <div>
        <ul className='graphLines'>
              <li  className={'show-graphLines'} >
                <span className={'show-graphLines-span-plain'} style={{  }}></span>All expiry
              </li>
              <li  className={'show-graphLines'} >
                <span className={'show-graphLines-span-dot'} style={{  }}></span>Selected expiry
              </li>
              <li  className={'show-graphLines'} >
                <span className={'show-graphLines-span-dash'} style={{  }}></span>Equity
              </li>
              <li  className={'show-graphLines'} >
                <span className={'show-graphLines-span-whitedot'} style={{  }}></span>CMP
              </li>
              <li  className={'show-graphLines'} >
                <span className={'show-graphLines-span-bluedot'} style={{  }}></span>Break Even Point
              </li>
        </ul>
      </div> */}

      {/* {underlyingData ? (
        <div className="StrategyBuilder_control__CvRj4">
          <div className="StrategyBuilder_input__header__Qqk0q">
            {underlyingData.length ? 'Equity:' : ''}
          </div>
          <div className="EquitiesList_equities__kFkji">
            {underlyingData.map((equity, index) => (
              <div
                key={index}
                className="EquitiesList_equity__dchK3"
                onContextMenu={(e) => handleRightClick(e, index)} // Handle right-click
              >
                <strong>{equity.side === 'B' ? 'Long' : 'Short'} {equity.shares}</strong>
                <span> shares at </span>
                <strong>${equity.avg_price_share}</strong>
              </div>
            ))}
          </div>
        </div>
      ) : (
        ''
      )} */}

      {/* Custom Context Menu */}
      {/* {showContextMenu && underlyingData[currentEditIndex] && (
        <div
          className="ContextMenu_container__Gv9l9 context-menu"
          style={{
            position: 'absolute',
            left: `${contextMenuPosition.x}px`,
            top: `${contextMenuPosition.y}px`,
            minWidth: '280px',
            maxWidth: '300px',
            opacity: 1,
            transition: 'opacity 150ms ease-in-out',
            border: '1px solid #ccc',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
            zIndex: 1000
          }}
        >
          <div className="ContextMenu_row__A_hbc ContextMenu_row--header__Ck4t9">
            {editValues.shares}× {underlyingData[currentEditIndex].side === 'B' ? 'Buy' : 'Sell'} {selectedSymbol}
          </div>
          <div className="ContextMenu_row__A_hbc ContextMenu_row--columns__Damcu">
            <div className="ContextMenu_column__8ueQs">
              <div>Quantity</div>
              <div className="PriceAndQuantityMenu_control__BPi17">
                <strong>
                  <input
                    type="number"
                    step="100"
                    value={editValues.shares}
                    onChange={(e) => setEditValues({ ...editValues, shares: e.target.value })}
                    className="EditableText_text__7BEoU"
                    style={{ width: '50px' }}
                  />
                </strong>
              </div>
            </div>
            <div className="ContextMenu_column__8ueQs">
              <div>Price</div>
              <div className="PriceAndQuantityMenu_control__BPi17">
                <strong>
                  $<input
                    type="number"
                    step="0.01"
                    value={editValues.avg_price_share}
                    onChange={(e) => setEditValues({ ...editValues, avg_price_share: e.target.value })}
                    className="EditableText_text__7BEoU"
                    style={{ width: '50px' }}
                  />
                </strong>
              </div>
            </div>
          </div>
          <div
            className="ContextMenu_row__A_hbc ContextMenu_row--button__iV4HO"
            role="button"
            onClick={handleSaveChanges}
          >
            <svg
              aria-hidden="true"
              focusable="false"
              data-prefix="far"
              data-icon="check"
              className="svg-inline--fa fa-check"
              role="img"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 448 512"
              style={{ marginRight: '5px' }}
            >
              <path
                fill="currentColor"
                d="M441 103c9.4 9.4 9.4 24.6 0 33.9L177 401c-9.4 9.4-24.6 9.4-33.9 0L7 265c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0l119 119L407 103c9.4-9.4 24.6-9.4 33.9 0z"
              ></path>
            </svg>
            Save Changes
          </div>

          <div onClick={handleRemoveEquity} className="ContextMenu_row__A_hbc ContextMenu_row--button__iV4HO" role="button" tabIndex="0">
          <svg
              aria-hidden="true"
              focusable="false"
              data-prefix="far"
              data-icon="xmark"
              className="svg-inline--fa fa-xmark"
              role="img"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 384 512"
              style={{ marginRight: '5px' }}
            >
              <path
                fill="currentColor"
                d="M345 137c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-119 119L73 103c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9l119 119L39 375c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0l119-119L311 409c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9l-119-119L345 137z"
              ></path>
            </svg>
            Remove </div>
          <div
            className="ContextMenu_row__A_hbc ContextMenu_row--button__iV4HO"
            role="button"
            onClick={() => setShowContextMenu(false)}
          >
             Close
          </div>
        </div>
      )} */}

      {/* custom-slider */}
        
<div className='sliderOver' style={{ display: `none` }}>
    <div className="slider">
      <div id="draggable">
        <div
          id="text-content"
          ref={textContentRef}
          onMouseDown={handleMouseDownslide}
          style={{ position: "relative" }}
          // style={{ position: "relative", left: `${textContentRefLeft}px` }}
        >

      <div className="slider-container" ref={sliderRef} style={{ width: `${screenWidth}px` }}>

        <div className="range-slider">
        {

  // Ensure min and max are valid numbers
  (min != null && max != null && !isNaN(min) && !isNaN(max) && Math.round(max) > Math.round(min)) ? (
    
    Array.from({ length: Math.round(max) - Math.round(min) + 1 }).map((_, index) => {
      const value = Math.round(min) + index; // Ensure value is an integer
      const percent = ((value - Math.round(min)) / (Math.round(max) - Math.round(min))) * 100; // Position in percentage
      const isMultipleOf5 = value % 5 === 0; // Check for multiples of 5
      const isMultipleOf10 = value % 10 === 0; // Check for multiples of 10

      return (
        <div
          key={value}
          className="slider-label"
          style={{
            position: 'absolute',
            left: `${percent}%`,
            transform: 'translateX(-50%)',
            top: isMultipleOf10 ? '-25px' : isMultipleOf5 ? '-20px' : '-15px',
            fontSize: isMultipleOf10 ? '12px' : isMultipleOf5 ? '10px' : '8px',
            fontWeight: isMultipleOf10 ? 'bold' : 'normal',
          }}
        >
          {isMultipleOf5 ? (isMultipleOf10 ? value : value) : '|'}
        </div>
      );
    })
  ) : (
    <div>Loading...</div> // Fallback message when min and max are invalid
  )
}

      </div>
      
      { 
  minStrikePrice > 0 && strikes?.length > 0 && strikes.map((group, groupIndex) => (
    group.values?.length > 0 && group.values.map((item, valueIndex) => {
      if (item.value <= 0) return null; // Skip rendering for invalid values

      // Generate unique color based on expiry
      // const uniqueColorMap = {};
      // const getColorForExpiry = (expiry) => {
      //   if (!expiry) return "#ddd"; // Default color for undefined expiry
      //   if (!uniqueColorMap[expiry]) {
      //     // Generate a random color for new expiry
      //     const color = `hsl(${Math.random() * 360}, 70%, 80%)`;
      //     uniqueColorMap[expiry] = color;
      //   }
      //   return uniqueColorMap[expiry];
      // };

      const backgroundColor = getExpiryColor(item.expiry);

      // return (
      //   <div
      //     key={`${groupIndex}-${valueIndex}`}
      //     className={`thumb thumb-${groupIndex}-${valueIndex} ${item.stype === 'C' ? 'green-thumb' : 'red-thumb'}`}
      //     onMouseDown={handleMouseDown(groupIndex, valueIndex)}
      //     onContextMenu={(event) => handleContextMenucus(event, groupIndex, valueIndex)}
      //     onClick={closePopup}
      //   >
      //     <div className={`value-block ${group.type === "Buy" ? 'above-slider' : 'below-slider'}`}>
      //       {(item.value)}
      //       <span className='strike-below'>
      //         {item.stype}
      //         {(item.avg_price_share_new === 0) ? (
      //           <span className="StrikeSelector_point__icon__km_Ta strike_icon">
      //             <svg
      //               aria-hidden="true"
      //               focusable="false"
      //               data-prefix="fas"
      //               data-icon="triangle-exclamation"
      //               className="svg-inline--fa fa-triangle-exclamation ItemIcons_icon__WDozd ItemIcons_icon--warning__TP2Sz ItemIcons_icon--shadow__6Vgg2"
      //               role="img"
      //               xmlns="http://www.w3.org/2000/svg"
      //               viewBox="0 0 512 512"
      //             >
      //               <path
      //                 fill="currentColor"
      //                 d="M256 32c14.2 0 27.3 7.5 34.5 19.8l216 368c7.3 12.4 7.3 27.7 .2 40.1S486.3 480 472 480L40 480c-14.3 0-27.6-7.7-34.7-20.1s-7-27.8 .2-40.1l216-368C228.7 39.5 241.8 32 256 32zm0 128c-13.3 0-24 10.7-24 24l0 112c0 13.3 10.7 24 24 24s24-10.7 24-24l0-112c0-13.3-10.7-24-24-24zm32 224a32 32 0 1 0 -64 0 32 32 0 1 0 64 0z"
      //               ></path>
      //             </svg>
      //           </span>
      //         ) : ''}
      //       </span>
      //       <span
      //         className='strike-qty'
      //         style={{ backgroundColor }}
      //       >
      //         x{item.quantity}
      //       </span>
      //     </div>
      //   </div>
      // );
    })
  ))
}


      </div>

      </div>
      </div>
    </div>
    </div>

        {/* Popup */}
        {/* {popupData && (
          <div className="popup" style={{ left: `${popupData.x}px`, top: `${popupData.y}px` }}>

            <div className="ContextMenu_container__Gv9l9 context-menu" style={{}}>
              <div className="ContextMenu_row__A_hbc ContextMenu_row--header__Ck4t9">{selectedSymbol} <span style={{ color: `${popupData.stype === 'C' ? '#00c038' : '#c20028'}` }}>{popupData.value}{popupData.stype}</span> <span style={{ color: 'yellow' }}>{popupData.expiry}</span></div>

              {(popupData.avg_price_share_new===0)?<div className="ContextMenu_row__A_hbc">
                <span className="OptionDetailsWarnings_warnings___6mR7"><svg aria-hidden="true" focusable="false" data-prefix="far" data-icon="triangle-exclamation" className="svg-inline--fa fa-triangle-exclamation " role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill="currentColor" d="M248.4 84.3c1.6-2.7 4.5-4.3 7.6-4.3s6 1.6 7.6 4.3L461.9 410c1.4 2.3 2.1 4.9 2.1 7.5c0 8-6.5 14.5-14.5 14.5l-387 0c-8 0-14.5-6.5-14.5-14.5c0-2.7 .7-5.3 2.1-7.5L248.4 84.3zm-41-25L9.1 385c-6 9.8-9.1 21-9.1 32.5C0 452 28 480 62.5 480l387 0c34.5 0 62.5-28 62.5-62.5c0-11.5-3.2-22.7-9.1-32.5L304.6 59.3C294.3 42.4 275.9 32 256 32s-38.3 10.4-48.6 27.3zM288 368a32 32 0 1 0 -64 0 32 32 0 1 0 64 0zm-8-184c0-13.3-10.7-24-24-24s-24 10.7-24 24l0 96c0 13.3 10.7 24 24 24s24-10.7 24-24l0-96z"></path></svg> Please change the strike price, this stike price is not available on this expiry.</span>
                </div>:''}
              
              <div className="ContextMenu_row__A_hbc ContextMenu_row--columns__Damcu">

                <div className="ContextMenu_column__8ueQs">
                  <div>Quantity</div>
                  <div className="PriceAndQuantityMenu_control__BPi17">
                    <span
                      className="icon-button PriceAndQuantityMenu_qty__icon__lbCxZ"
                      role="button"
                      tabIndex="0"
                      onClick={() => handleQuantityChangepop(-1)} // Decrease quantity
                    >
                      <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="circle-chevron-left" className="svg-inline--fa fa-circle-chevron-left" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                        <path fill="currentColor" d="M512 256A256 256 0 1 0 0 256a256 256 0 1 0 512 0zM271 135c9.4-9.4 24.6-9.4 33.9 0s9.4 24.6 0 33.9l-87 87 87 87c9.4 9.4 9.4 24.6 0 33.9s-24.6 9.4-33.9 0L167 273c-9.4-9.4-9.4-24.6 0-33.9L271 135z" />
                      </svg>
                    </span>
                    <strong className="PriceAndQuantityMenu_qty__value__xB0h1">
                      <span data-editable="true" tabIndex="0" role="button" aria-label="Edit" className="EditableText_host__RKtXH">
                        <span className="EditableText_text__7BEoU" tabIndex="0">{popupData.quantity}</span>
                      </span>
                    </strong>
                    <span
                      className="icon-button PriceAndQuantityMenu_qty__icon__lbCxZ"
                      role="button"
                      tabIndex="0"
                      onClick={() => handleQuantityChangepop(1)} // Increase quantity
                    >
                      <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="circle-chevron-right" className="svg-inline--fa fa-circle-chevron-right" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                        <path fill="currentColor" d="M0 256a256 256 0 1 0 512 0A256 256 0 1 0 0 256zM241 377c-9.4 9.4-24.6 9.4-33.9 0s-9.4-24.6 0-33.9l87-87-87-87c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0L345 239c9.4 9.4 9.4 24.6 0 33.9L241 377z" />
                      </svg>
                    </span>
                  </div>
                </div>

                <div className="ContextMenu_column__8ueQs">
                  <div>Price</div>
                  <div className="PriceAndQuantityMenu_control__BPi17">
                    <strong>
                      $<span data-editable="true" tabIndex="0" role="button" aria-label="Edit" className="EditableText_host__RKtXH">
                        <span className="EditableText_text__7BEoU" tabIndex="0">{(thumbMoved)?popupData.avg_price_share_new:popupData.avg_price_share}</span>
                      </span>
                    </strong>
                  </div>
                </div>
              </div>
              <div className="ContextMenu_row__A_hbc OptionDetails_greeks__iIe26">
                {[
                  { label: "Bid", value: bidprice, color: "green" },
                  { label: "Ask", value: askprice, color: "red" },
                  { label: "Volume", value: volume },
                  { label: "OI", value: oi },
                  { label: "IV", value: iv.toFixed(2)+'%' },
                  { label: "Delta", value: delta },
                  { label: "Theta", value: theta },
                  { label: "Gamma", value: gamma },
                  { label: "Vega", value: vega },
                  { label: "Rho", value: rho },
                ].map((greek, index) => (
                  <div className="OptionDetails_greek__IMSKG" key={index}>
                    <span>{greek.label} </span>
                    <span className={`OptionDetails_greek__value__WLRnm ${greek.color || ""}`}>{greek.value}</span>
                  </div>
                ))}
              </div>

              <div className="ContextMenu_row__A_hbc">
                  <label className='expiry-select' htmlFor="expiry-select">Change Expiration</label>
                  <select
                      id="expiry-select"
                      value={popupData.expiry || ''}
                      onChange={(e) => handleExpiryChangePopup(e.target.value)}
                      className="expiry-dropdown"
                  >
                      {allExpiries.map((expiry, idx) => (
                          <option key={idx} value={expiry}>
                              {expiry}
                          </option>
                      ))}
                  </select>
              </div>

              <div onClick={removeSliderValue} className="ContextMenu_row__A_hbc ContextMenu_row--button__iV4HO" role="button" tabIndex="0">
              <svg
              aria-hidden="true"
              focusable="false"
              data-prefix="far"
              data-icon="xmark"
              className="svg-inline--fa fa-xmark"
              role="img"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 384 512"
              style={{ marginRight: '5px' }}
            >
              <path
                fill="currentColor"
                d="M345 137c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-119 119L73 103c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9l119 119L39 375c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0l119-119L311 409c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9l-119-119L345 137z"
              ></path>
            </svg>
                Remove </div>
              <div onClick={closePopup} className="ContextMenu_row__A_hbc ContextMenu_row--button__iV4HO" role="button" tabIndex="0">Close </div>

            </div>
          </div>
        )} */}

      {/* <Carousel
        swipeable={true}
        draggable={true}
        showDots={false}
        responsive={responsive}
        ssr={true} // means to render carousel on server-side.
        infinite={true}
        autoPlay={false}
        autoPlaySpeed={1000}
        keyBoardControl={true}
        customTransition="all .5"
        transitionDuration={2000}
        containerClass="carousel-container"
        removeArrowOnDeviceType={["tablet", "mobile"]}
        dotListClass="custom-dot-list-style"
        itemClass="carousel-item-padding-40-px"
      >
        <div>Premium deployed: ${premium_deployed.toFixed(0)}</div>
        <div>Max Profit: ${max_profit.toFixed(0)}</div>
      </Carousel> */}

      {/* <Line data={chartData} options={chartOptions}/> */}
          
      <div className="table-container">
      <div className="md-6">
  <h3>Table 1 - Unmerged Options</h3>
  <table border="1" width="100%" style={{ borderCollapse: "collapse" }}>
    <thead>
      <tr>
        <th>QTY</th>
        <th>Account</th>
        <th>Symbol</th>
        <th>Type</th>
        <th>Value</th>
        <th>Actual QTY</th>
        <th>Expiry</th>
        <th>Action</th>
      </tr>
    </thead>
    <tbody>
      {tableData.map((val, index) => {
        // Set background color based on the 'type' value
        const rowBackgroundColor =
          val.type === "Buy Call" || val.type === "Buy Put"
            ? "green"
            : val.type === "Sell Call" || val.type === "Sell Put"
            ? "red"
            : "transparent";

        return (
          <tr key={index} >
            <td>
              <input
                type="number"
                value={val.inputQuantity}
                min="1"
                max={val.quantity}
                onChange={(e) => handleQuantityChange(index, Number(e.target.value))}
              />
            </td>
            <td>{val.acct_name}</td>
            <td>{val.symbol}</td>
            <td style={{ backgroundColor: rowBackgroundColor }}>{val.type}</td>
            <td>{val.value}</td>
            <td>{val.quantity}</td>
            <td style={{ backgroundColor: getExpiryColor(val.expiry) }}>{val.expiry}</td>
            <td>
              <button className='button' onClick={() => moveToTable2(index)}>Move</button>
            </td>
          </tr>
        );
      })}
    </tbody>
  </table>
</div>


<div className="md-6">
  <h3>Table 2 - Merged Options</h3>
  <table border="1" width="100%" style={{ borderCollapse: "collapse" }}>
    <thead>
      <tr>
        <th>Symbol</th>
        <th>Type</th>
        <th>Value</th>
        <th>QTY</th>
        <th>Expiry</th>
        <th>Action</th>
      </tr>
    </thead>
    <tbody>
      {table2Data.map((val, index) => {
        const rowBackgroundColor =
          val.type === "Buy Call" || val.type === "Buy Put"
            ? "green"
            : val.type === "Sell Call" || val.type === "Sell Put"
            ? "red"
            : "transparent";

        return (
          <tr key={index}>
            <td>{val.symbol}</td>
            <td>{val.type}</td>
            <td style={{ backgroundColor: rowBackgroundColor }}>{val.value}</td>
            <td>{val.quantity}</td>
            <td style={{ backgroundColor: getExpiryColor(val.expiry) }}>{val.expiry}</td>
            <td>
              <button className='button' onClick={() => unmoveFromTable2(index)}>Unmove</button>
            </td>
          </tr>
        );
      })}
    </tbody>
  </table>
  <div className='centerAlign'>
  <button className='button' onClick={mergeToTable3} disabled={table2Data.length === 0}>
    Merge
  </button>
  </div>
</div>


    </div>

    <div className="table-container">
  <div className="md-6">
    <h3>Table 3 - Final Merged Groups</h3>
    <table className='text-center' border="1" width="100%" style={{ borderCollapse: "collapse" }}>
      <thead>
        <tr>
          <th>Group</th>
          <th>Symbol</th>
          <th>Type</th>
          <th>Value</th>
          <th>QTY</th>
          <th>Expiry</th>
        </tr>
      </thead>
      <tbody>
  {Object.entries(
    table3Data.reduce((acc, row) => {
      acc[row.group] = acc[row.group] || [];
      acc[row.group].push(row);
      return acc;
    }, {})
  ).map(([group, rows]) => (
    <React.Fragment key={group}>
      {/* Group Header Row */}
      <tr>
        <td
          rowSpan={rows.length + 1} // Extra row for Remove button
          style={{
            backgroundColor: "black",
            color: "white",
            border: "2px solid white",
            fontWeight: "bold",
            textAlign: "center",
          }}
        >
        <div> <span className='grp_name'>{group}</span></div> 

          <button className='button' onClick={() => removeFromTable3(group)}>Remove Group</button>
        </td>
        <td>{rows[0].symbol}</td>
        <td
          style={{
            backgroundColor:
              rows[0].type === "Buy Call" || rows[0].type === "Buy Put"
                ? "green"
                : rows[0].type === "Sell Call" || rows[0].type === "Sell Put"
                ? "red"
                : "transparent",
            color: "white",
          }}
        >
          {rows[0].type}
        </td>
        <td>{rows[0].value}</td>
        <td>{rows[0].quantity}</td>
        <td style={{ backgroundColor: getExpiryColor(rows[0].expiry) }}>{rows[0].expiry}</td>
      </tr>

      {/* Other Rows in the Group */}
      {rows.slice(1).map((val, index) => (
        <tr key={`${group}-${index + 1}`}>
          <td>{val.symbol}</td>
          <td
            style={{
              backgroundColor:
                val.type === "Buy Call" || val.type === "Buy Put"
                  ? "green"
                  : val.type === "Sell Call" || val.type === "Sell Put"
                  ? "red"
                  : "transparent",
              color: "white",
            }}
          >
            {val.type}
          </td>
          <td>{val.value}</td>
          <td>{val.quantity}</td>
          <td style={{ backgroundColor: getExpiryColor(val.expiry) }}>{val.expiry}</td>
        </tr>
      ))}

      {/* White Border After Each Group */}
      <tr>
        <td colSpan="7" style={{ borderBottom: "2px solid white" }}></td>
      </tr>
    </React.Fragment>
  ))}
</tbody>

    </table>
  </div>
</div>



      {/* <div className='sliderWi' style={{ marginTop: '20px' }}>
        <h4>Adjust Display Range</h4>
        <Slider
          value={sliderRange}
          onChange={handleRangeSliderChange}
          valueLabelDisplay="on"
          valueLabelFormat={(value) => `+-${value}%`}
          min={1}
          max={50}
          marks
          disableSwap
        />
      </div>

      <div className='sliderWi' >
          <h4>Date</h4>
          <Slider
        value={date}
        onChange={handleSliderChange}
        valueLabelDisplay="on"
        valueLabelFormat={(value) => formatDateLabel(value)} // Convert days to a formatted date
        min={0}
        max={maxDays} // Replace maxDays with your actual maxDays variable
        marks
      />
          
        </div>

      <div className='d-flex gap10 sliderWi'>
        <div className='slide' style={{ width: '100%'}}>
          <h4>Implied Volatility</h4>
          <Slider
            value={impliedVolatility}
            onChange={(event, newValue) => setImpliedVolatility(newValue)}
            valueLabelDisplay="on"
            valueLabelFormat={(value) => `${(value===0)?'At Respective Volatility':'Volatility: '+value+'%'}`}
            min={0}
            max={100}
            marks
          />
        </div>

        <div className='slide' style={{ width: '100%' }}>
          <h4>Risk Free Rate</h4>
          <Slider
            value={riskFreeRate}
            onChange={(event, newValue) => setriskFreeRate(newValue)}
            valueLabelDisplay="on"
            valueLabelFormat={(value) => `Rate: ${value}%`}
            min={0}
            max={10}
            step={.01}
            marks
          />
        </div>

      </div> */}

    </div>
  );
};

export default OptionsGraphWithMultiStrikeSlider;
